// @ts-nocheck
/*
 * CTIAgentV2Page — /cti-preview
 *
 * Byte-for-byte port of the "Signal CTI" artifact (db5329f7). Pure mock,
 * no APIs. Five state views (idle / ringing / oncall / wrap / break)
 * driven by the state tabs in the topbar; the sidebar's presence dot,
 * state pill, and title mirror whichever view is active. Bottom sticky
 * Call HUD appears only on the On-call view.
 *
 * The artifact defaults to dark; here we honour the app theme store
 * instead so the toggle in the topbar flips the whole cloud v2 shell,
 * not just this page.
 *
 * Portaled to document.body so the AppShell chrome doesn't leak through.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { getUser, getUserAccessToken } from '@/utilities/setting';
import { loginAsProfile, logoutAsProfile } from '@/features/cti/softphone/loginAsProfile';
import { postApiCall } from '@/service/httpCall';
import { API_AGENT_MOBILE_DIAL, API_AGENT_MY_ASSIGNED_CLIS } from '@/service/url';
import { BACKEND_API_BASE_URL } from '@/utilities/constant';

/* ────────────────────────────────────────────────────────────
 * State catalogue (verbatim from artifact <script>)
 * ────────────────────────────────────────────────────────── */
const STATE_MAP = {
  idle:    { label: 'Ready',   title: 'Available',   time: 'In state for 04:12', cls: 'available' },
  ringing: { label: 'Ringing', title: 'Incoming',    time: 'Ringing for 00:04',  cls: 'ringing' },
  oncall:  { label: 'Talking', title: 'On Call',     time: 'Talk time 04:23',    cls: 'on-call' },
  wrap:    { label: 'Wrap',    title: 'Wrap-up',     time: 'Wrap for 00:42',     cls: 'wrap' },
  break:   { label: 'Break',   title: 'Lunch break', time: 'Break for 12:37',    cls: 'break' },
};

/* ────────────────────────────────────────────────────────────
 * Icon set — inline SVG so paths match the artifact exactly.
 * ────────────────────────────────────────────────────────── */
const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const Icon = ({ children, size = 12, w = 24, h = 24, thick = 2 }) => (
  <svg viewBox={`0 0 ${w} ${h}`} width={size} height={size} {...stroke} strokeWidth={thick}>
    {children}
  </svg>
);

const IcSignal    = (p) => <Icon {...p}><path d="M2 12h3l2-6 4 12 2-6h3l2-3 2 3h2" /></Icon>;
const IcPhone     = (p) => <Icon {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.9.35 1.85.59 2.81.72A2 2 0 0122 16.92z" /></Icon>;
const IcClock     = (p) => <Icon {...p}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></Icon>;
const IcSearch    = (p) => <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></Icon>;
const IcMoon      = (p) => <Icon {...p}><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></Icon>;
const IcSun       = (p) => <Icon {...p}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></Icon>;
const IcMail      = (p) => <Icon {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" /></Icon>;
const IcFile      = (p) => <Icon {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></Icon>;
const IcClose     = (p) => <Icon {...p}><path d="M18 6L6 18M6 6l12 12" /></Icon>;
const IcCheck2    = (p) => <Icon {...p}><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></Icon>;
const IcSparkle   = (p) => <Icon {...p}><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" /></Icon>;
const IcSquares   = (p) => <Icon {...p}><path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" /></Icon>;
const IcMic       = (p) => <Icon {...p}><path d="M12 2a3 3 0 00-3 3v7a3 3 0 006 0V5a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2M12 19v3" /></Icon>;
const IcHold      = (p) => <Icon {...p}><path d="M6 4h4v16H6zM14 4h4v16h-4z" /></Icon>;
const IcTransfer  = (p) => <Icon {...p}><path d="M17 3l4 4-4 4M3 7h18M7 21l-4-4 4-4M21 17H3" /></Icon>;
const IcUsers     = (p) => <Icon {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></Icon>;
const IcKeypad    = (p) => <Icon {...p}><circle cx="6" cy="6" r="1" fill="currentColor" /><circle cx="12" cy="6" r="1" fill="currentColor" /><circle cx="18" cy="6" r="1" fill="currentColor" /><circle cx="6" cy="12" r="1" fill="currentColor" /><circle cx="12" cy="12" r="1" fill="currentColor" /><circle cx="18" cy="12" r="1" fill="currentColor" /><circle cx="6" cy="18" r="1" fill="currentColor" /><circle cx="12" cy="18" r="1" fill="currentColor" /><circle cx="18" cy="18" r="1" fill="currentColor" /></Icon>;
const IcEnd       = (p) => <Icon {...p} thick={2.5}><path d="M10.68 13.31a16 16 0 003.4 2.4l1.5-1.5c.6-.6 1.5-.8 2.3-.5 1.1.4 2.4.7 3.7.7.9 0 1.6.7 1.6 1.6v2.4c0 .9-.7 1.6-1.6 1.6C9.6 20 4 14.4 4 7.4c0-.9.7-1.6 1.6-1.6H8c.9 0 1.6.7 1.6 1.6 0 1.3.2 2.6.7 3.7.3.8.1 1.7-.5 2.3z" /></Icon>;
const IcArrowIn   = (p) => <Icon {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 013.19 5.18 2 2 0 015.11 3h3a2 2 0 012 1.72" /></Icon>;
const IcInfo      = (p) => <Icon {...p}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></Icon>;
const IcTriangle  = (p) => <Icon {...p} thick={0}><path d="M12 2l10 20H2z" fill="currentColor" /></Icon>;
const IcPause     = (p) => <Icon {...p} thick={2.5}><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 2" /></Icon>;
const IcPlaySolid = (p) => <Icon {...p}><path d="M5 3l14 9-14 9V3z" fill="currentColor" stroke="none" /></Icon>;
const IcTinyCheck = (p) => <Icon {...p} thick={2.5}><path d="M5 12l5 5L20 7" /></Icon>;
const IcHeadset   = (p) => <Icon {...p} thick={2.5}><path d="M6 2l3 6-2 2c1 4 4 7 8 8l2-2 6 3-1 4c0 1-1 2-2 2C10 25 0 15 0 5c0-1 1-2 2-2z" /></Icon>;

/* ────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────── */
export default function CTIAgentV2Page() {
  const [view, setView] = useState('idle'); // idle | ringing | oncall | wrap | break
  const s = STATE_MAP[view];

  // Theme is fully local to the CTI (no cross-talk with the shell).
  // Defaults to light so the page opens on the palette the artifact was designed
  // around; the button in the topbar flips it to dark on demand.
  const [isDark, setIsDark] = useState(false);
  const toggleTheme = () => setIsDark((v) => !v);

  // Scroll-lock the shell body while the portal is mounted.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* --------- Telephony login / logout (real API wiring) --------- */

  // Lazy-load the softphone module once so its initDailer() binds the
  // SIP handlers to hidden `keypadConnect2` / `disconnect2` buttons. This
  // is what loginAsProfile('WEBRTC') then clicks to connect + register.
  useEffect(() => {
    let cancelled = false;
    import('@/features/cti/softphone/demo-1').catch((err) => {
      if (!cancelled) console.error('[cti-v2] Softphone bootstrap failed', err);
    });
    return () => { cancelled = true; };
  }, []);

  const [userId] = useState(() => {
    try { return getUser()?.id || null; } catch { return null; }
  });
  const configuredProfiles = useMemo(() => {
    try {
      const list = getUser()?.agentData?.agentTelephony?.profile || [];
      return new Set(list.map((p) => p?.type).filter(Boolean));
    } catch { return new Set(); }
  }, []);
  const [activeProfileType, setActiveProfileType] = useState(() => {
    try { return sessionStorage.getItem('activeLoginType'); } catch { return null; }
  });
  const [pendingProfileType, setPendingProfileType] = useState(null);

  // Sync UI when the helper fires 'sessionStorageChange' or another tab flips it.
  useEffect(() => {
    const sync = () => {
      try { setActiveProfileType(sessionStorage.getItem('activeLoginType')); } catch {}
    };
    window.addEventListener('sessionStorageChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('sessionStorageChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const [agentId] = useState(() => {
    try { return getUser()?.agentData?.id || null; } catch { return null; }
  });

  /* --- Outbound dialer --- */
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [dialing, setDialing] = useState(false);
  const [assignedClis, setAssignedClis] = useState([]);
  const [recentOpen, setRecentOpen] = useState(false);

  /* Popover call lifecycle. `null` = dialer view (input + keypad).
   * 'dialing' | 'ringing' | 'oncall' | 'wrapup' = in-call chrome (timer + controls).
   * Mocked here so the flow demos even without the softphone actually
   * bridging; when the real callStore lands we'll drive this from it. */
  const [popCall, setPopCall] = useState(null);
  const [popCallSecs, setPopCallSecs] = useState(0);
  const [popMute, setPopMute] = useState(false);
  const [popHold, setPopHold] = useState(false);
  const [popSubOpen, setPopSubOpen] = useState(null); // 'keypad' | 'transfer' | 'conference' | null
  const [popDtmf, setPopDtmf] = useState('');

  // Timer — increments while call is oncall.
  useEffect(() => {
    if (popCall !== 'oncall') return undefined;
    const id = setInterval(() => setPopCallSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [popCall]);

  // Mock transition: dialing → oncall after 1.6s. Real integration would
  // drive this from callStore's callState instead.
  useEffect(() => {
    if (popCall !== 'dialing') return undefined;
    const id = setTimeout(() => setPopCall('oncall'), 1600);
    return () => clearTimeout(id);
  }, [popCall]);

  const fmtCallSecs = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const endPopCall = () => {
    setPopCall(null);
    setPopCallSecs(0);
    setPopMute(false);
    setPopHold(false);
    setPopSubOpen(null);
    setPopDtmf('');
  };

  /* Draggable position for the dial popover. `null` = attached to the topbar
   * button (default). Once the user drags, the popover switches to fixed
   * absolute positioning at (dialPos.x, dialPos.y) and stays there until the
   * popover is closed. */
  const [dialPos, setDialPos] = useState(null);
  const dragRef = useRef(null); // { dx, dy } during a drag
  const startDrag = (e) => {
    const target = e.currentTarget.closest('.dial-pop');
    if (!target) return;
    const rect = target.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    // Seed position immediately so the popover pins to viewport coordinates
    // instead of jumping when we switch off relative-to-button positioning.
    setDialPos({ x: rect.left, y: rect.top });
    e.preventDefault();
  };
  useEffect(() => {
    if (!dragRef.current) return undefined;
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { dx, dy } = dragRef.current;
      // Clamp so the popover can't slide fully off-screen.
      const x = Math.max(4, Math.min(window.innerWidth - 60,  e.clientX - dx));
      const y = Math.max(4, Math.min(window.innerHeight - 60, e.clientY - dy));
      setDialPos({ x, y });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dialPos]);
  // Reset the drag position each time the popover is re-opened so it
  // re-anchors below the Dial button (rather than reappearing at its last
  // dragged spot from a previous session).
  useEffect(() => {
    if (!dialerOpen) {
      setDialPos(null);
      setRecentOpen(false);
      endPopCall();
    }
  }, [dialerOpen]);
  // Fetch CLIs once (needed as the `from` number when Mobile-profile dials).
  useEffect(() => {
    let cancelled = false;
    const token = getUserAccessToken?.();
    fetch(`${BACKEND_API_BASE_URL}${API_AGENT_MY_ASSIGNED_CLIS.replace(BACKEND_API_BASE_URL, '')}`, {
      headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (cancelled || !j) return;
        const list = Array.isArray(j) ? j : (j?.data || []);
        setAssignedClis(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Recent-calls mock (would come from API_AGENT_DASHBOARD_CALLS in the real page).
  const RECENT_DIALS = [
    { name: 'Anjali Menon',   number: '+91 98430 22417', at: '18:31' },
    { name: 'Vikas Kumar',    number: '+91 99671 20884', at: '17:44' },
    { name: 'Sana Qureshi',   number: '2201',            at: '17:21' },
    { name: 'Rakesh Nair',    number: '+91 98330 55127', at: '16:47' },
    { name: 'Priya Raghavan', number: '2204',            at: '15:12' },
  ];

  const handleDial = useCallback(async (numberArg) => {
    const raw = String(numberArg ?? dialNumber ?? '').replace(/[^0-9+#*]/g, '');
    if (!raw) { toast.error('Enter a number to dial'); return; }
    if (!activeProfileType) { toast.error('Log in to a telephony profile first'); return; }
    if (dialing) return;

    const type = activeProfileType;
    setDialing(true);
    try {
      if (type === 'WEBRTC' || type === 'Soft Phone') {
        // v1's softphone module wires a click handler onto `#call`; the trick
        // is to write the target into `#numberToCall` first (creating the hidden
        // elements on the fly if they don't exist), then click `#call`.
        let numInput = document.getElementById('numberToCall');
        if (!numInput) {
          numInput = document.createElement('input');
          numInput.setAttribute('id', 'numberToCall');
          numInput.setAttribute('type', 'hidden');
          document.body.appendChild(numInput);
        }
        numInput.value = raw;
        let callBtn = document.getElementById('call');
        if (!callBtn) {
          callBtn = document.createElement('button');
          callBtn.setAttribute('id', 'call');
          callBtn.style.display = 'none';
          document.body.appendChild(callBtn);
        }
        callBtn.click();
        toast.success(`Dialing ${raw}…`);
      } else if (type === 'Mobile') {
        if (!agentId) { toast.error('Agent id missing'); return; }
        const cli = assignedClis[0];
        if (!cli) { toast.error('No CLI assigned — cannot dial via Mobile'); return; }
        const fromNumber = cli.actual_number || cli.number || cli.masking_number;
        await postApiCall(API_AGENT_MOBILE_DIAL, {
          agent_id: agentId,
          destination: raw.startsWith('+') ? raw : `+${raw}`,
          from: fromNumber?.startsWith('+') ? fromNumber : `+${fromNumber}`,
        });
        toast.success(`Mobile dialer: calling ${raw}`);
      } else {
        toast.error(`${type} does not support outbound dialing yet`);
        return;
      }
      // Keep the popover open; flip it into the "call in progress" view.
      // A real integration would let callStore drive popCall — for now we
      // enter 'dialing', and the effect above transitions to 'oncall'.
      setPopCall('dialing');
      setPopCallSecs(0);
      setRecentOpen(false);
    } catch (err) {
      toast.error(err?.message || 'Dial failed');
      console.error('[cti-v2] dial failed', err);
    } finally {
      setDialing(false);
    }
  }, [dialNumber, activeProfileType, agentId, assignedClis, dialing]);

  const handleToggleProfile = useCallback(async (type) => {
    if (!userId) { toast.error('No user id — reload and try again'); return; }
    if (pendingProfileType) return;
    if (!configuredProfiles.has(type)) {
      toast.error(`${type} is not configured on your account`);
      return;
    }
    setPendingProfileType(type);
    try {
      // Same profile is active → treat as logout.
      if (activeProfileType === type) {
        await logoutAsProfile(userId, type);
        return;
      }
      // Switch — log the current one out first so both server + softphone
      // reach a clean state before the new register.
      if (activeProfileType && activeProfileType !== type) {
        try { await logoutAsProfile(userId, activeProfileType); }
        catch (err) { console.warn('[cti-v2] prior logout failed', err); }
      }
      await loginAsProfile(userId, type);
    } catch (err) {
      /* helpers already toasted the specific message */
      console.error('[cti-v2] profile switch failed', err);
    } finally {
      setPendingProfileType(null);
    }
  }, [userId, activeProfileType, pendingProfileType, configuredProfiles]);

  const shell = (
    <div className="signal-cti" data-theme={isDark ? 'dark' : 'light'}>
      <StyleTag />

      <div className="app">
        {/* ============ SIDEBAR ============ */}
        <aside className="sidebar">
          <div className="agent">
            <div className="avatar">
              RS
              <span className={`presence ${s.cls}`} />
            </div>
            <div className="agent-meta">
              <div className="agent-name">Rohan Sharma</div>
              <div className="agent-ext num">Ext · 40214</div>
            </div>
          </div>

          <div className="state-panel">
            <div className="state-label">
              <span className={`state-dot ${s.cls}`} />
              <span>{s.label}</span>
            </div>
            <div className="state-title">{s.title}</div>
            <div className="state-time num">{s.time}</div>
            <div className="state-actions">
              <button className="sc-btn">
                <IcHeadset size={12} />
                Change status
              </button>
            </div>
          </div>

          {/* Telephony device — WEBRTC / Soft Phone / Mobile login/logout.
              Same click semantics as /cti-signal: click active = logout,
              click inactive = switch. */}
          <div className="device-panel">
            <div className="device-caption">
              <span>Device</span>
              {activeProfileType ? (
                <span className="device-status ok">
                  <span className="device-dot ok" />
                  {activeProfileType} connected
                </span>
              ) : (
                <span className="device-status">
                  <span className="device-dot off" />
                  Not connected
                </span>
              )}
            </div>
            <div className="device-tabs" role="group" aria-label="Telephony device">
              {[
                { key: 'WEBRTC',     label: 'WebRTC',    hint: 'Browser softphone' },
                { key: 'Soft Phone', label: 'Softphone', hint: 'Desktop client' },
                { key: 'Mobile',     label: 'Mobile',    hint: 'Ring your registered mobile number' },
              ].map((d) => {
                const active     = activeProfileType === d.key;
                const configured = configuredProfiles.has(d.key);
                const busy       = pendingProfileType === d.key;
                const disabled   = !configured || Boolean(pendingProfileType);
                return (
                  <button
                    key={d.key}
                    onClick={() => handleToggleProfile(d.key)}
                    disabled={disabled}
                    title={
                      !configured
                        ? `${d.label} is not configured on your account`
                        : active
                          ? `Click to log out of ${d.label}`
                          : d.hint
                    }
                    className={`device-tab ${active ? 'active' : ''} ${!configured ? 'unavailable' : ''}`}
                  >
                    {busy ? <span className="device-spin" /> : d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="kpi-grid">
            <div className="kpi">
              <div className="kpi-label">Calls</div>
              <div className="kpi-value num">47</div>
              <div className="kpi-delta up">↑ 4 vs avg</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Avg AHT</div>
              <div className="kpi-value num">4:12</div>
              <div className="kpi-delta">team 4:38</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Wrap %</div>
              <div className="kpi-value num">18%</div>
              <div className="kpi-delta">target &lt;20</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">CSAT</div>
              <div className="kpi-value num">4.7</div>
              <div className="kpi-delta up">↑ 0.3</div>
            </div>
          </div>

          <div className="broadcast">
            <div className="broadcast-from">Supervisor · Meera</div>
            Focus on H1 renewals today. Legal note on policy L-4471 pinned in wiki.
          </div>

          <div className="sidebar-bottom">
            <div className="conn-row">
              <span className="signal-bars">
                <span className="signal-bar" />
                <span className="signal-bar" />
                <span className="signal-bar" />
                <span className="signal-bar" />
              </span>
              Line clear · 42ms
            </div>
            <div className="conn-row">
              <IcClock size={12} />
              Shift 09:00 – 18:00 · 3h left
            </div>
          </div>
        </aside>

        {/* ============ MAIN ============ */}
        <div className="main">
          <div className="topbar">
            <div className="search">
              <IcSearch size={14} />
              Find caller, ticket, campaign…
              <span className="search-kbd">⌘K</span>
            </div>

            <div className="state-tabs" role="tablist">
              {[
                { k: 'idle',    label: 'Ready',   dotColor: undefined },
                { k: 'ringing', label: 'Ringing', dotColor: 'var(--sky)' },
                { k: 'oncall',  label: 'On Call', dotColor: 'var(--emerald)' },
                { k: 'wrap',    label: 'Wrap',    dotColor: 'var(--amber)' },
                { k: 'break',   label: 'Break',   dotColor: 'var(--rose)' },
              ].map((t) => (
                <button
                  key={t.k}
                  className={`state-tab ${view === t.k ? 'active' : ''}`}
                  onClick={() => { setView(t.k); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  <span className="dot" style={t.dotColor ? { color: t.dotColor } : undefined} />
                  {t.label}
                </button>
              ))}
            </div>

            <div className="dial-wrap">
              <button
                className={`dial-btn ${dialerOpen ? 'active' : ''}`}
                onClick={() => setDialerOpen((v) => !v)}
                title="Place an outbound call"
              >
                <IcPhone size={13} thick={2.5} />
                Dial
              </button>
              {dialerOpen && (
                <>
                  {/* Only show the scrim while the popover is attached to the
                   * topbar. Once the user drags it, treat it like a floating
                   * softphone: no scrim, page underneath is interactive. */}
                  {!dialPos && (
                    <div className="dial-scrim" onClick={() => setDialerOpen(false)} />
                  )}
                  <div
                    className={`dial-pop ${dialPos ? 'floating' : ''} ${recentOpen ? 'has-recent' : ''}`}
                    style={dialPos ? { top: dialPos.y, left: dialPos.x, right: 'auto' } : undefined}
                  >
                    <div
                      className="dial-pop-head dial-drag-handle"
                      onMouseDown={startDrag}
                      title="Drag to move"
                    >
                      <span className="dial-drag-grip" aria-hidden>
                        <span /><span /><span /><span /><span /><span />
                      </span>
                      <span className="dial-pop-title">New call</span>
                      <button
                        className={`dial-recent-toggle ${recentOpen ? 'on' : ''}`}
                        onClick={() => setRecentOpen((v) => !v)}
                        onMouseDown={(e) => e.stopPropagation()}
                        title={recentOpen ? 'Hide recent calls' : 'Show recent calls'}
                        aria-pressed={recentOpen}
                      >
                        <IcClock size={11} />
                        <span>Recent</span>
                        <span className={`dial-caret ${recentOpen ? 'open' : ''}`} aria-hidden>›</span>
                      </button>
                      <button
                        className="dial-pop-close"
                        onClick={() => setDialerOpen(false)}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <IcClose size={12} thick={2.5} />
                      </button>
                    </div>

                    {popCall ? (
                      /* ── In-call view ─────────────────────────────── */
                      <div className="popcall">
                        <div className="popcall-caller">
                          <div className="popcall-avatar">
                            {(dialNumber || '?').slice(-2)}
                          </div>
                          <div className="popcall-body">
                            <div className="popcall-name">
                              {popCall === 'dialing' ? 'Dialing…' :
                               popCall === 'ringing' ? 'Ringing…' :
                               popCall === 'oncall'  ? 'Connected' :
                                                       'Wrap-up'}
                            </div>
                            <div className="popcall-num num">{dialNumber || '—'}</div>
                          </div>
                          <div className="popcall-timer num">
                            {popCall === 'dialing'
                              ? <span className="device-spin" />
                              : fmtCallSecs(popCallSecs)}
                          </div>
                        </div>

                        <div className="popcall-ctrls">
                          <button
                            className={`popcall-btn ${popMute ? 'on' : ''}`}
                            title="Mute (M)"
                            onClick={() => setPopMute((v) => !v)}
                            disabled={popCall !== 'oncall'}
                          >
                            <IcMic size={15} />
                          </button>
                          <button
                            className={`popcall-btn ${popHold ? 'on' : ''}`}
                            title="Hold (H)"
                            onClick={() => setPopHold((v) => !v)}
                            disabled={popCall !== 'oncall'}
                          >
                            <IcHold size={15} />
                          </button>
                          <button
                            className={`popcall-btn ${popSubOpen === 'transfer' ? 'on' : ''}`}
                            title="Transfer (T)"
                            onClick={() => setPopSubOpen((v) => v === 'transfer' ? null : 'transfer')}
                            disabled={popCall !== 'oncall'}
                          >
                            <IcTransfer size={15} />
                          </button>
                          <button
                            className={`popcall-btn ${popSubOpen === 'conference' ? 'on' : ''}`}
                            title="Conference"
                            onClick={() => setPopSubOpen((v) => v === 'conference' ? null : 'conference')}
                            disabled={popCall !== 'oncall'}
                          >
                            <IcUsers size={15} />
                          </button>
                          <button
                            className={`popcall-btn ${popSubOpen === 'keypad' ? 'on' : ''}`}
                            title="Keypad · DTMF"
                            onClick={() => setPopSubOpen((v) => v === 'keypad' ? null : 'keypad')}
                            disabled={popCall !== 'oncall'}
                          >
                            <IcKeypad size={15} />
                          </button>
                          <button
                            className="popcall-btn end"
                            title="End call"
                            onClick={endPopCall}
                          >
                            <IcEnd size={14} />
                            End
                          </button>
                        </div>

                        {/* Sub-panels toggled by the row above */}
                        {popSubOpen === 'keypad' && (
                          <div className="popcall-sub">
                            <div className="popcall-sub-head">Send DTMF</div>
                            <div className="popcall-dtmf num">{popDtmf || '—'}</div>
                            <div className="dial-keypad">
                              {['1','2','3','4','5','6','7','8','9','*','0','#'].map((k) => (
                                <button
                                  key={k}
                                  className="dial-key num"
                                  onClick={() => setPopDtmf((v) => (v + k).slice(-16))}
                                >
                                  {k}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {(popSubOpen === 'transfer' || popSubOpen === 'conference') && (
                          <div className="popcall-sub">
                            <div className="popcall-sub-head">
                              {popSubOpen === 'transfer' ? 'Transfer to' : 'Add to conference'}
                            </div>
                            <div className="popcall-agents">
                              {[
                                { name: 'Sana Qureshi',    ext: '2201', state: 'available' },
                                { name: 'Network L2 Desk', ext: '5100', state: 'available' },
                                { name: 'Billing Support', ext: '5310', state: 'onCall' },
                                { name: 'Bhavana Reddy',   ext: '4033', state: 'available' },
                              ].map((a) => (
                                <div key={a.ext} className="popcall-agent">
                                  <span className={`popcall-agent-dot ${a.state === 'available' ? 'ok' : 'busy'}`} />
                                  <span className="popcall-agent-body">
                                    <span className="popcall-agent-name">{a.name}</span>
                                    <span className="popcall-agent-ext num">x{a.ext}</span>
                                  </span>
                                  {popSubOpen === 'transfer' ? (
                                    <span className="popcall-agent-btns">
                                      <button className="hud-pop-btn primary" onClick={() => setPopSubOpen(null)}>Warm</button>
                                      <button
                                        className="hud-pop-btn"
                                        disabled={a.state !== 'available'}
                                        onClick={() => setPopSubOpen(null)}
                                      >Blind</button>
                                    </span>
                                  ) : (
                                    <button className="hud-pop-btn primary" onClick={() => setPopSubOpen(null)}>Add</button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                    <div className="dial-pop-body">
                      {/* Left column — number input + keypad + call button */}
                      <div className="dial-pop-main">
                        <div className="dial-display num">
                          <input
                            value={dialNumber}
                            onChange={(e) => setDialNumber(e.target.value.replace(/[^0-9+#*]/g, ''))}
                            placeholder="Enter number"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleDial(); }}
                          />
                          {dialNumber && (
                            <button
                              className="dial-clear"
                              onClick={() => setDialNumber((v) => v.slice(0, -1))}
                              title="Backspace"
                              aria-label="Backspace"
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                                <path d="M18 9l-6 6M12 9l6 6" />
                              </svg>
                            </button>
                          )}
                        </div>

                        <div className="dial-keypad">
                          {['1','2','3','4','5','6','7','8','9','*','0','#'].map((k) => (
                            <button
                              key={k}
                              className="dial-key num"
                              onClick={() => setDialNumber((v) => v + k)}
                            >
                              {k}
                            </button>
                          ))}
                        </div>

                        <button
                          className="dial-call-btn"
                          disabled={dialing || !dialNumber || !activeProfileType}
                          onClick={() => handleDial()}
                        >
                          {dialing ? (
                            <>
                              <span className="device-spin" />
                              Dialing…
                            </>
                          ) : (
                            <>
                              <IcPhone size={14} thick={2.5} />
                              Call
                            </>
                          )}
                        </button>

                        {!activeProfileType && (
                          <div className="dial-hint">
                            Log in to WebRTC / Softphone / Mobile in the sidebar to enable dialing.
                          </div>
                        )}
                      </div>

                      {/* Right column — recent calls (collapsed by default) */}
                      {recentOpen && (
                        <div className="dial-pop-side">
                          <div className="dial-recent-head">Recent</div>
                          <div className="dial-recent">
                            {RECENT_DIALS.map((r) => (
                              <button
                                key={r.number}
                                className="dial-recent-row"
                                onClick={() => setDialNumber(r.number.replace(/[^0-9+#*]/g, ''))}
                              >
                                <div className="dial-recent-body">
                                  <div className="dial-recent-name">{r.name}</div>
                                  <div className="dial-recent-num num">{r.number}</div>
                                </div>
                                <div className="dial-recent-at num">{r.at}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="theme-toggle-seg" role="group" aria-label="Theme">
              <button
                className={`theme-seg-btn ${!isDark ? 'active' : ''}`}
                onClick={() => setIsDark(false)}
                aria-pressed={!isDark}
                title="Light"
              >
                <IcSun size={13} />
              </button>
              <button
                className={`theme-seg-btn ${isDark ? 'active' : ''}`}
                onClick={() => setIsDark(true)}
                aria-pressed={isDark}
                title="Dark"
              >
                <IcMoon size={13} />
              </button>
            </div>
          </div>

          <div className="canvas">
            {view === 'idle'    && <IdleView />}
            {view === 'ringing' && <RingingView />}
            {view === 'oncall'  && <OnCallView setView={setView} />}
            {view === 'wrap'    && <WrapView />}
            {view === 'break'   && <BreakView />}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(shell, document.body);
}

/* ────────────────────────────────────────────────────────────
 * Views
 * ────────────────────────────────────────────────────────── */

function IdleView() {
  return (
    <div className="view active">
      <div className="ready-toggle">
        <div className="ready-info">
          <div className="ready-title">Available</div>
          <div className="ready-sub">Ready for the next call · 3 in queue · avg wait 42s</div>
        </div>
        <button className="ready-btn">
          <IcPause size={14} />
          Pause after this
        </button>
      </div>

      <div className="idle-grid">
        <div className="sc-card">
          <div className="card-head">
            <div className="card-title">Callback queue</div>
            <span className="card-count">3</span>
          </div>
          <div className="queue-item">
            <span className="queue-priority" />
            <div className="queue-avatar">AM</div>
            <div className="queue-meta">
              <div className="queue-name">Anjali Menon</div>
              <div className="queue-reason">Renewal · policy L-4471</div>
            </div>
            <div className="queue-time num">3m ago</div>
          </div>
          <div className="queue-item">
            <span className="queue-priority medium" />
            <div className="queue-avatar q2">VK</div>
            <div className="queue-meta">
              <div className="queue-name">Vikas Kumar</div>
              <div className="queue-reason">Refund request · ticket #9812</div>
            </div>
            <div className="queue-time num">11m ago</div>
          </div>
          <div className="queue-item">
            <span className="queue-priority low" />
            <div className="queue-avatar q3">SP</div>
            <div className="queue-meta">
              <div className="queue-name">Sonia Patel</div>
              <div className="queue-reason">Info · new plan pricing</div>
            </div>
            <div className="queue-time num">28m ago</div>
          </div>
        </div>

        <div className="sc-card">
          <div className="card-head">
            <div className="card-title">My leads today</div>
            <span className="card-count">12</span>
          </div>
          <div className="queue-item">
            <div className="queue-avatar q4">RN</div>
            <div className="queue-meta">
              <div className="queue-name">Rakesh Nair</div>
              <div className="queue-reason">Warm · showed interest yesterday</div>
            </div>
            <div className="queue-time num">Ready</div>
          </div>
          <div className="queue-item">
            <div className="queue-avatar q2">KT</div>
            <div className="queue-meta">
              <div className="queue-name">Kavya Thakur</div>
              <div className="queue-reason">Follow-up · demo scheduled</div>
            </div>
            <div className="queue-time num">2pm</div>
          </div>
          <div className="queue-item">
            <div className="queue-avatar">DS</div>
            <div className="queue-meta">
              <div className="queue-name">Devansh Shah</div>
              <div className="queue-reason">Cold · matched profile</div>
            </div>
            <div className="queue-time num">Ready</div>
          </div>
        </div>

        <div className="sc-card">
          <div className="card-head">
            <div className="card-title">Recent activity</div>
            <span className="card-count">5</span>
          </div>
          <div className="queue-item">
            <div className="queue-avatar q3">MG</div>
            <div className="queue-meta">
              <div className="queue-name">Meera Gupta</div>
              <div className="queue-reason">Resolved · 6:12 · CSAT 5★</div>
            </div>
            <div className="queue-time num">14:22</div>
          </div>
          <div className="queue-item">
            <div className="queue-avatar q4">AB</div>
            <div className="queue-meta">
              <div className="queue-name">Arjun Bansal</div>
              <div className="queue-reason">Callback set · Tue 10am</div>
            </div>
            <div className="queue-time num">13:47</div>
          </div>
          <div className="queue-item">
            <div className="queue-avatar q2">PT</div>
            <div className="queue-meta">
              <div className="queue-name">Priya Thomas</div>
              <div className="queue-reason">Escalated · billing team</div>
            </div>
            <div className="queue-time num">13:15</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RingingView() {
  return (
    <div className="view active">
      <div className="ringing-hero">
        <div className="caller-visual">
          <div className="sonar">
            <span className="sonar-ring" />
            <span className="sonar-ring" />
            <span className="sonar-ring" />
            <div className="caller-avatar">AM</div>
          </div>
          <div className="caller-name">Anjali Menon</div>
          <div className="caller-num num">+91 98430 22417</div>
        </div>

        <div className="caller-info">
          <div className="badge-row">
            <span className="badge priority">
              <IcTriangle size={10} />
              Priority
            </span>
            <span className="badge repeat">3rd call this week</span>
            <span className="badge tier">Gold · since 2019</span>
            <span className="badge campaign">H1 Renewals</span>
          </div>

          <div className="info-grid">
            <div className="info-cell">
              <div className="info-label">Campaign</div>
              <div className="info-value">H1 Renewals · Wave 2</div>
            </div>
            <div className="info-cell">
              <div className="info-label">Route</div>
              <div className="info-value">IVR · Renewals queue</div>
            </div>
            <div className="info-cell">
              <div className="info-label">Language</div>
              <div className="info-value">English / Hindi</div>
            </div>
            <div className="info-cell">
              <div className="info-label">Timezone</div>
              <div className="info-value num">IST · 15:34</div>
            </div>
          </div>

          <div className="prefetch-title">Recent interactions · pre-fetched from CRM</div>
          <div className="history-list">
            <div className="history-item">
              <div className="history-icon"><IcPhone size={12} /></div>
              <div className="history-body">
                <div className="history-head">
                  <div className="history-what">Called about renewal pricing</div>
                  <div className="history-when num">Yesterday · 11:20</div>
                </div>
                <div className="history-detail">Wanted the family plan quote · resolved · CSAT 4★</div>
              </div>
            </div>
            <div className="history-item">
              <div className="history-icon"><IcMail size={12} /></div>
              <div className="history-body">
                <div className="history-head">
                  <div className="history-what">Sent quote email</div>
                  <div className="history-when num">Yesterday · 12:04</div>
                </div>
                <div className="history-detail">Family plan · ₹18,400/yr · valid till 31 Aug</div>
              </div>
            </div>
            <div className="history-item">
              <div className="history-icon"><IcFile size={12} /></div>
              <div className="history-body">
                <div className="history-head">
                  <div className="history-what">Policy L-4471 · expires in 12 days</div>
                  <div className="history-when">Auto-flag</div>
                </div>
                <div className="history-detail">Grace period ends 26 Aug · notice sent 05 Aug</div>
              </div>
            </div>
          </div>

          <div className="ringing-actions">
            <button className="ring-btn reject">
              <IcClose size={16} thick={2.5} />
              Reject
            </button>
            <button className="ring-btn answer">
              <IcPhone size={16} thick={2.5} />
              Answer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OnCallView({ setView }) {
  const [mute, setMute]           = useState(false);
  const [hold, setHold]           = useState(true);
  const [popover, setPopover]     = useState(null); // 'keypad' | 'transfer' | 'conference' | null
  const [dtmf, setDtmf]           = useState('');
  const KEYS = ['1','2','3','4','5','6','7','8','9','*','0','#'];
  const AGENTS = [
    { name: 'Sana Qureshi',   role: 'Team Leader',       ext: '2201', state: 'available' },
    { name: 'Priya Raghavan',  role: 'Team Leader · Team A', ext: '2204', state: 'onCall' },
    { name: 'Network L2 Desk', role: 'Escalation',       ext: '5100', state: 'available' },
    { name: 'Billing Support', role: 'Skill queue',      ext: '5310', state: 'onCall' },
    { name: 'Bhavana Reddy',   role: 'Agent · Team A',   ext: '4033', state: 'available' },
  ];
  const toggle = (kind) => setPopover((p) => (p === kind ? null : kind));
  const closePop = () => setPopover(null);

  return (
    <div className="view active">
      <div className="sentiment-strip">
        <div className="sentiment-label">Sentiment</div>
        <div className="sentiment-bar">
          <div className="sentiment-marker" />
        </div>
        <div className="sentiment-value">Positive · engaged</div>
      </div>

      <div className="oncall-grid">
        <div className="oncall-left">
          <div className="script">
            <div className="script-title">
              <div className="card-title">Script · Renewal price objection</div>
              <div className="script-progress">
                <span className="script-step done" />
                <span className="script-step done" />
                <span className="script-step active" />
                <span className="script-step" />
                <span className="script-step" />
              </div>
            </div>
            <div className="script-body">
              <p>
                I hear you — renewals are up ~8% this year, but on your family plan we're actually holding the price. Let me confirm the numbers you saw yesterday: <span className="script-highlight">₹18,400 for the year</span>, and you'd keep the same four dependents.
              </p>
              <p>
                If you'd like, I can add the <span className="script-highlight">outpatient rider</span> for another ₹2,100 — it's what most of your peers on the Gold tier are picking this cycle.
              </p>
            </div>
            <div className="script-check">
              <IcCheck2 size={14} thick={2.5} />
              Recording notice acknowledged at 00:04
            </div>
          </div>

          <div className="disposition">
            <div className="disposition-head">
              <div className="disposition-title">Disposition</div>
              <div className="ai-badge">
                <IcSparkle size={10} thick={2.5} />
                Auto-suggested
              </div>
            </div>
            <div className="dispo-row">
              <div className="dispo-label">Outcome</div>
              <div className="dispo-chips">
                <span className="chip selected">Renewal confirmed</span>
                <span className="chip ai-suggest">+ Add rider</span>
                <span className="chip">Callback</span>
                <span className="chip">Escalate</span>
              </div>
            </div>
            <div className="dispo-row">
              <div className="dispo-label">Reason</div>
              <div className="dispo-chips">
                <span className="chip">Objection · price</span>
                <span className="chip selected">Objection · resolved</span>
                <span className="chip">Info request</span>
              </div>
            </div>
            <div className="dispo-row">
              <div className="dispo-label">Follow-up</div>
              <input className="dispo-input" defaultValue="Send confirmation email with rider details" />
            </div>
          </div>
        </div>

        <div className="oncall-right">
          <div className="customer-card">
            <div className="customer-head">
              <div className="hud-avatar" style={{ width: 44, height: 44, fontSize: 14 }}>AM</div>
              <div className="customer-info">
                <div className="customer-name">Anjali Menon</div>
                <div className="customer-tier">Gold · customer since 2019</div>
              </div>
            </div>
            <div className="customer-stats">
              <div className="stat-cell">
                <div className="stat-label">Lifetime</div>
                <div className="stat-value num">₹4.2L</div>
              </div>
              <div className="stat-cell">
                <div className="stat-label">Policy</div>
                <div className="stat-value num">L-4471</div>
              </div>
              <div className="stat-cell">
                <div className="stat-label">Expires in</div>
                <div className="stat-value num">12 days</div>
              </div>
              <div className="stat-cell">
                <div className="stat-label">Last CSAT</div>
                <div className="stat-value">4★ · Aug 12</div>
              </div>
            </div>

            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-icon"><IcArrowIn size={11} /></div>
                <div className="timeline-body">
                  <div className="timeline-what">Renewal quote call · 4m 12s</div>
                  <div className="timeline-when">Yesterday · 11:20</div>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon"><IcSquares size={11} /></div>
                <div className="timeline-body">
                  <div className="timeline-what">Quote email delivered</div>
                  <div className="timeline-when">Yesterday · 12:04</div>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon"><IcClock size={11} /></div>
                <div className="timeline-body">
                  <div className="timeline-what">Auto-reminder scheduled</div>
                  <div className="timeline-when">21 Aug · 10:00</div>
                </div>
              </div>
            </div>
          </div>

          <div className="ai-assist">
            <div className="ai-head">
              <IcSparkle size={12} thick={2.5} />
              Copilot nudge
            </div>
            <div className="ai-nudge">
              She just asked about <b>dependent coverage</b>. Policy L-4471 covers 4 dependents up to 25 yrs. The outpatient rider bumps optical &amp; dental — mention it.
            </div>
          </div>
        </div>
      </div>

      {/* ------ Call HUD (sticky bottom, fully interactive) ------ */}
      {popover && <div className="hud-scrim" onClick={closePop} />}
      <div className="call-hud">
        <div className="hud-caller">
          <div className="hud-avatar">AM</div>
          <div>
            <div className="hud-name">Anjali Menon</div>
            <div className="hud-timer num">04:23</div>
          </div>
        </div>
        <div className="hud-controls">

          <button
            className={`hud-btn ${mute ? 'active' : ''}`}
            title="Mute (M)"
            onClick={() => setMute((v) => !v)}
          >
            <IcMic size={16} />
          </button>

          <button
            className={`hud-btn ${hold ? 'active' : ''}`}
            title="Hold (H)"
            onClick={() => setHold((v) => !v)}
          >
            <IcHold size={16} />
          </button>

          {/* Transfer */}
          <div className="hud-btn-wrap">
            <button
              className={`hud-btn ${popover === 'transfer' ? 'active' : ''}`}
              title="Transfer (T)"
              onClick={() => toggle('transfer')}
            >
              <IcTransfer size={16} />
            </button>
            {popover === 'transfer' && (
              <div className="hud-pop">
                <div className="hud-pop-head">Transfer call</div>
                <div className="hud-pop-list">
                  {AGENTS.map((a) => (
                    <div key={a.ext} className="hud-pop-row">
                      <span className={`hud-pop-dot ${a.state === 'available' ? 'ok' : 'busy'}`} />
                      <div className="hud-pop-body">
                        <div className="hud-pop-name">{a.name}</div>
                        <div className="hud-pop-sub num">{a.role} · x{a.ext}</div>
                      </div>
                      <div className="hud-pop-btns">
                        <button className="hud-pop-btn primary" onClick={closePop}>Warm</button>
                        <button
                          className="hud-pop-btn"
                          disabled={a.state !== 'available'}
                          onClick={closePop}
                        >
                          Blind
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Conference */}
          <div className="hud-btn-wrap">
            <button
              className={`hud-btn ${popover === 'conference' ? 'active' : ''}`}
              title="Conference"
              onClick={() => toggle('conference')}
            >
              <IcUsers size={16} />
            </button>
            {popover === 'conference' && (
              <div className="hud-pop">
                <div className="hud-pop-head">Add to conference</div>
                <div className="hud-pop-list">
                  {AGENTS.map((a) => (
                    <div key={a.ext} className="hud-pop-row">
                      <span className={`hud-pop-dot ${a.state === 'available' ? 'ok' : 'busy'}`} />
                      <div className="hud-pop-body">
                        <div className="hud-pop-name">{a.name}</div>
                        <div className="hud-pop-sub num">{a.role} · x{a.ext}</div>
                      </div>
                      <button className="hud-pop-btn primary" onClick={closePop}>Add</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Keypad */}
          <div className="hud-btn-wrap">
            <button
              className={`hud-btn ${popover === 'keypad' ? 'active' : ''}`}
              title="Keypad"
              onClick={() => toggle('keypad')}
            >
              <IcKeypad size={16} />
            </button>
            {popover === 'keypad' && (
              <div className="hud-pop keypad-pop">
                <div className="hud-pop-head">
                  <span>Send DTMF</span>
                  {dtmf && (
                    <button className="keypad-clear" onClick={() => setDtmf('')}>Clear</button>
                  )}
                </div>
                <div className="keypad-display num">{dtmf || 'Press a key'}</div>
                <div className="keypad-grid">
                  {KEYS.map((k) => (
                    <button
                      key={k}
                      className="keypad-key num"
                      onClick={() => setDtmf((d) => (d + k).slice(-16))}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            className="hud-btn end"
            title="End call"
            onClick={() => { closePop(); setView('wrap'); }}
          >
            <IcEnd size={14} />
            End
          </button>
        </div>
        <div className="hud-quality">
          <span className="signal-bars">
            <span className="signal-bar" />
            <span className="signal-bar" />
            <span className="signal-bar" />
            <span className="signal-bar" />
          </span>
          HD · 40ms
        </div>
      </div>
    </div>
  );
}

function WrapView() {
  return (
    <div className="view active">
      <div className="wrap-hero">
        <div className="wrap-main">
          <div className="wrap-header">
            <div className="wrap-caller">
              <div className="hud-avatar" style={{ width: 48, height: 48, fontSize: 15 }}>AM</div>
              <div>
                <div className="wrap-caller-name">Anjali Menon</div>
                <div className="wrap-caller-meta">
                  <span>Call · 05:41</span>
                  <span>Policy L-4471</span>
                  <span>H1 Renewals</span>
                </div>
              </div>
            </div>
            <div className="wrap-timer">
              <div className="wrap-timer-num">00:42</div>
              <div className="wrap-timer-label">Wrap time</div>
            </div>
          </div>

          <div className="wrap-summary">
            <div className="wrap-summary-head">
              <div className="wrap-summary-title">Auto-transcribed summary</div>
              <div className="ai-badge">
                <IcSparkle size={10} thick={2.5} />
                From transcript
              </div>
            </div>
            <div className="wrap-summary-body">
              Anjali confirmed renewal on policy <b>L-4471</b> at ₹18,400/yr. Added <b>outpatient rider</b> (+₹2,100). Asked to receive confirmation via email and a copy of the rider terms. No escalation. Reminder set for 21 Aug to check payment link.
            </div>
          </div>

          <div className="disposition" style={{ border: '1px solid var(--border)', padding: '16px 0 0 0' }}>
            <div className="dispo-row" style={{ paddingTop: 0 }}>
              <div className="dispo-label">Outcome</div>
              <div className="dispo-chips">
                <span className="chip selected">Renewal confirmed · rider added</span>
              </div>
            </div>
            <div className="dispo-row">
              <div className="dispo-label">Follow-up</div>
              <input className="dispo-input" defaultValue="Send confirmation email with rider details" />
            </div>
            <div className="dispo-row" style={{ paddingBottom: 0, borderBottom: 'none' }}>
              <div className="dispo-label">Notes</div>
              <input className="dispo-input" defaultValue="Anjali may bring in husband's plan (H-2201) next quarter." />
            </div>
          </div>

          <div className="wrap-actions">
            <button className="wrap-btn ghost">Save &amp; stay in wrap</button>
            <button className="wrap-btn primary">
              <IcTinyCheck size={14} />
              Ready for next
            </button>
          </div>
        </div>

        <div className="wrap-side">
          <div className="wrap-metric">
            <div className="wrap-metric-head">
              <div className="wrap-metric-title">This call</div>
            </div>
            <div className="wrap-mini-stats">
              <div className="wrap-mini">
                <div className="wrap-mini-value num">5:41</div>
                <div className="wrap-mini-label">Talk time</div>
              </div>
              <div className="wrap-mini">
                <div className="wrap-mini-value num" style={{ color: 'var(--emerald)' }}>+82</div>
                <div className="wrap-mini-label">Sentiment</div>
              </div>
              <div className="wrap-mini">
                <div className="wrap-mini-value num">2</div>
                <div className="wrap-mini-label">Objections</div>
              </div>
              <div className="wrap-mini">
                <div className="wrap-mini-value num" style={{ color: 'var(--emerald)' }}>✓</div>
                <div className="wrap-mini-label">Compliance</div>
              </div>
            </div>
          </div>

          <div className="wrap-metric">
            <div className="wrap-metric-head">
              <div className="wrap-metric-title">Coming next</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Queue is <b style={{ color: 'var(--text)' }}>3 waiting · avg 42s</b>. Next up is <b style={{ color: 'var(--text)' }}>Vikas Kumar</b>, refund request. Take a breath — you've got a moment.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BreakView() {
  return (
    <div className="view active">
      <div className="break-hero">
        <div className="break-title">You're on break</div>
        <div className="break-sub">Lunch · started 12:14</div>
        <div className="break-clock num">17:23</div>
        <div className="break-elapsed">Remaining · out of 30:00</div>
        <div className="break-actions">
          <button className="break-btn extend">
            <IcClock size={15} />
            Extend 5 min
          </button>
          <button className="break-btn resume">
            <IcPlaySolid size={15} thick={2.5} />
            Go back to Ready
          </button>
        </div>
        <div className="break-nudge">
          <IcInfo size={16} />
          You've handled 47 calls in 6 hours — that's a heavy morning. Take the full break; queue coverage is fine.
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * Scoped stylesheet — verbatim from artifact, prefixed to .signal-cti
 * ────────────────────────────────────────────────────────── */
function StyleTag() {
  return (
    <style dangerouslySetInnerHTML={{ __html: `
      /* Light palette — cloud v2's silver-blue NEUMORPHIC base. Everything
       * sits on the same #e4e9f2 tone; depth comes entirely from a two-tone
       * shadow (dark bottom-right + light top-left inset), never from tonal
       * contrast. Matches globals.css :root exactly. */
      .signal-cti {
        --ground: hsl(218, 32%, 92%);          /* v2 --background & --card (#e4e9f2) */
        --surface: hsl(218, 32%, 92%);         /* SAME as ground — cards match page */
        --surface-2: hsl(218, 32%, 92%);       /* Nested surfaces also same tone;
                                                  the two-tone inset shadow gives
                                                  them their "pressed in" look. */
        --border: transparent;                 /* Neumorphic — no visible hairline */
        --border-strong: hsl(218, 22%, 82%);
        --text: hsl(222, 47%, 11%);            /* v2 --foreground (slate-900) */
        --text-muted: hsl(215, 25%, 32%);      /* v2 --muted-foreground (slate-700) */
        --text-dim: hsl(215, 16%, 47%);
        --accent: hsl(222, 47%, 17%);          /* v2 --primary (deep navy) */
        --accent-soft: hsl(222, 47%, 17%, 0.08);
        --accent-ring: hsl(222, 47%, 17%, 0.22);
        --emerald: hsl(142, 71%, 36%);
        --emerald-soft: hsl(142, 71%, 36%, 0.10);
        --amber: hsl(38, 92%, 50%);
        --amber-soft: hsl(38, 92%, 50%, 0.10);
        --rose: hsl(0, 72%, 51%);
        --rose-soft: hsl(0, 72%, 51%, 0.10);
        --sky: hsl(217, 91%, 60%);
        --sky-soft: hsl(217, 91%, 60%, 0.10);
        /* Neumorphic shadows — verbatim from globals.css .card / .input-embossed. */
        --raise-shadow:
          10px 10px 28px rgba(163, 177, 198, 0.6),
          -10px -10px 28px rgba(255, 255, 255, 0.95);
        --raise-shadow-sm:
          4px 4px 10px rgba(163, 177, 198, 0.55),
          -4px -4px 10px rgba(255, 255, 255, 0.9);
        --inset-shadow:
          inset 4px 4px 8px rgba(163, 177, 198, 0.6),
          inset -4px -4px 8px rgba(255, 255, 255, 0.95);
        --inset-shadow-sm:
          inset 2px 2px 5px rgba(163, 177, 198, 0.55),
          inset -2px -2px 5px rgba(255, 255, 255, 0.9);
        --shadow-sm: var(--raise-shadow-sm);
        --shadow-md: var(--raise-shadow);
        position: fixed; inset: 0; z-index: 1000; overflow: auto;
        background: var(--ground); color: var(--text);
        font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        font-size: 14px; line-height: 1.5; letter-spacing: -0.01em;
        -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility;
      }
      /* Dark palette — distinct card tones + simple drop shadow (the flavour
       * that was already working). The neumorphic two-tone shadow only reads
       * on a light ground, so dark reverts to the classical raised-card look
       * cloud v2's .dark uses. */
      .signal-cti[data-theme="dark"] {
        --ground: hsl(222, 47%, 8%);           /* v2 dark --background */
        --surface: hsl(222, 47%, 11%);         /* v2 dark --card */
        --surface-2: hsl(217, 33%, 17%);       /* v2 dark --muted */
        --border: hsl(217, 25%, 30%);          /* v2 dark --border */
        --border-strong: hsl(217, 25%, 42%);
        --text: hsl(210, 40%, 98%);
        --text-muted: hsl(215, 30%, 78%);
        --text-dim: hsl(215, 20%, 55%);
        --accent: hsl(217, 91%, 68%);
        --accent-soft: hsl(217, 91%, 68%, 0.14);
        --accent-ring: hsl(217, 91%, 68%, 0.32);
        --emerald: hsl(142, 71%, 45%);
        --emerald-soft: hsl(142, 71%, 45%, 0.14);
        --amber: hsl(38, 92%, 55%);
        --amber-soft: hsl(38, 92%, 55%, 0.14);
        --rose: hsl(0, 62%, 55%);
        --rose-soft: hsl(0, 62%, 55%, 0.14);
        --sky: hsl(217, 91%, 68%);
        --sky-soft: hsl(217, 91%, 68%, 0.14);
        /* Dark uses standard drop / inset shadows — the neumorphic two-tone
         * pattern requires a light ground to bounce highlight off, so on a
         * dark ground we swap it for a straightforward shadow language and
         * let the tonal contrast between --ground / --surface / --surface-2
         * carry the visual hierarchy. */
        --raise-shadow: 0 1px 2px hsl(0, 0%, 0%, 0.35), 0 6px 20px hsl(0, 0%, 0%, 0.35);
        --raise-shadow-sm: 0 1px 2px hsl(0, 0%, 0%, 0.35), 0 2px 8px hsl(0, 0%, 0%, 0.35);
        --inset-shadow: inset 0 1px 2px hsl(0, 0%, 0%, 0.35);
        --inset-shadow-sm: inset 0 1px 2px hsl(0, 0%, 0%, 0.25);
        --shadow-sm: var(--raise-shadow-sm);
        --shadow-md: var(--raise-shadow);
      }
      .signal-cti *, .signal-cti *::before, .signal-cti *::after {
        box-sizing: border-box;
        /* Reset shell inheritance — the AppShell + tailwind base sometimes
         * push a muted foreground on descendants, which washed out
         * .card-title / .queue-name to near invisible. Force everything
         * inside the scope to fall back to the CTI's --text token unless a
         * more specific rule below overrides. */
        color: inherit;
      }
      .signal-cti { color: var(--text); }

      /* Neutralise every legacy Bootstrap-shim / shadcn / Tailwind rule that
       * targets bare <button>. The scoped .ring-btn / .hud-btn / .wrap-btn /
       * .sc-btn selectors below then paint the actual styling on top of a
       * clean baseline. */
      .signal-cti button {
        background: transparent; border: none; margin: 0; padding: 0;
        font: inherit; color: inherit; text-align: inherit;
        box-shadow: none; border-radius: 0; cursor: pointer;
        min-width: 0; line-height: inherit; letter-spacing: inherit;
      }
      .signal-cti .num, .signal-cti .mono {
        font-family: ui-monospace, "SF Mono", Menlo, "Cascadia Code", Consolas, monospace;
        font-variant-numeric: tabular-nums; letter-spacing: -0.02em;
      }
      .signal-cti .eyebrow {
        text-transform: uppercase; letter-spacing: 0.14em;
        font-size: 10px; font-weight: 600; color: var(--text-dim);
      }

      .signal-cti .app { display: grid; grid-template-columns: 240px 1fr; min-height: 100vh; background: var(--ground); }

      /* Sidebar */
      .signal-cti .sidebar {
        background: var(--surface); border-right: 1px solid var(--border);
        padding: 20px 16px; display: flex; flex-direction: column; gap: 18px;
        position: sticky; top: 0; height: 100vh; overflow-y: auto;
      }
      .signal-cti .brand { display: flex; align-items: center; gap: 10px; padding-bottom: 16px; border-bottom: 1px solid var(--border); }
      .signal-cti .brand-mark {
        width: 26px; height: 26px; border-radius: 6px; background: var(--accent);
        display: grid; place-items: center; color: var(--surface);
      }
      .signal-cti .brand-name { font-size: 14px; font-weight: 700; letter-spacing: -0.02em; }
      .signal-cti .brand-sub { font-size: 10px; color: var(--text-dim); letter-spacing: 0.08em; text-transform: uppercase; margin-top: 1px; }

      .signal-cti .agent { display: flex; align-items: center; gap: 10px; }
      .signal-cti .avatar {
        width: 40px; height: 40px; border-radius: 50%;
        background: linear-gradient(135deg, #f59e0b, #ec4899);
        color: #fff; display: grid; place-items: center;
        font-weight: 700; font-size: 14px; letter-spacing: 0; position: relative;
      }
      .signal-cti .presence {
        position: absolute; right: -1px; bottom: -1px;
        width: 12px; height: 12px; border-radius: 50%; border: 2px solid var(--surface);
      }
      .signal-cti .presence.available { background: var(--emerald); }
      .signal-cti .presence.ringing   { background: var(--sky); animation: signal-pulse 1.2s infinite ease-out; }
      .signal-cti .presence.on-call   { background: var(--emerald); }
      .signal-cti .presence.wrap      { background: var(--amber); }
      .signal-cti .presence.break     { background: var(--rose); }
      .signal-cti .agent-meta { min-width: 0; }
      .signal-cti .agent-name { font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .signal-cti .agent-ext { font-size: 11px; color: var(--text-dim); letter-spacing: 0.02em; }

      .signal-cti .state-panel {
        border-radius: 10px; background: var(--surface-2); border: 1px solid var(--border);
        padding: 12px; display: flex; flex-direction: column; gap: 8px;
      }
      .signal-cti .state-label { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; color: var(--text-muted); }
      .signal-cti .state-dot { width: 8px; height: 8px; border-radius: 50%; }
      .signal-cti .state-dot.available { background: var(--emerald); box-shadow: 0 0 0 3px var(--emerald-soft); }
      .signal-cti .state-dot.ringing   { background: var(--sky); box-shadow: 0 0 0 3px var(--sky-soft); }
      .signal-cti .state-dot.on-call   { background: var(--emerald); box-shadow: 0 0 0 3px var(--emerald-soft); }
      .signal-cti .state-dot.wrap      { background: var(--amber); box-shadow: 0 0 0 3px var(--amber-soft); }
      .signal-cti .state-dot.break     { background: var(--rose); box-shadow: 0 0 0 3px var(--rose-soft); }
      .signal-cti .state-title { font-size: 16px; font-weight: 700; letter-spacing: -0.02em; }
      .signal-cti .state-time { font-size: 11px; color: var(--text-dim); }
      .signal-cti .state-actions { display: flex; flex-direction: column; gap: 6px; margin-top: 4px; }

      .signal-cti .sc-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 6px;
        padding: 7px 10px; border-radius: 8px; font-weight: 500; font-size: 12px;
        cursor: pointer; border: 1px solid transparent; background: transparent;
        color: var(--text-muted); transition: background 120ms ease, color 120ms ease;
        font-family: inherit;
      }
      .signal-cti .sc-btn:hover { background: var(--surface); color: var(--text); }

      /* Telephony device switcher (sidebar) */
      .signal-cti .device-panel {
        display: flex; flex-direction: column; gap: 8px;
        padding: 12px; border-radius: 12px;
        background: var(--surface); color: var(--text);
        box-shadow: var(--raise-shadow-sm);
      }
      .signal-cti .device-caption {
        display: flex; align-items: center; justify-content: space-between;
        gap: 8px; font-size: 11px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.08em;
        color: var(--text-dim);
      }
      .signal-cti .device-status {
        display: inline-flex; align-items: center; gap: 6px;
        font-size: 10px; text-transform: none; letter-spacing: 0.02em;
        color: var(--text-muted); font-weight: 500;
      }
      .signal-cti .device-status.ok { color: var(--text); }
      .signal-cti .device-dot {
        width: 6px; height: 6px; border-radius: 50%; background: var(--text-dim);
      }
      .signal-cti .device-dot.ok {
        background: var(--emerald);
        box-shadow: 0 0 0 3px var(--emerald-soft);
      }
      .signal-cti .device-dot.off { background: var(--rose); opacity: 0.6; }

      .signal-cti .device-tabs {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px;
        padding: 2px; border-radius: 8px;
        background: var(--surface); box-shadow: var(--inset-shadow-sm);
      }
      .signal-cti .device-tab {
        display: inline-flex; align-items: center; justify-content: center;
        min-height: 28px; padding: 4px 6px;
        border: none; border-radius: 6px; background: transparent;
        color: var(--text-muted); font-family: inherit;
        font-size: 11px; font-weight: 500; letter-spacing: 0.01em;
        transition: color 120ms, background 120ms, box-shadow 140ms;
        cursor: pointer;
      }
      .signal-cti .device-tab:not(:disabled):hover { color: var(--text); }
      .signal-cti .device-tab:disabled { cursor: not-allowed; }
      .signal-cti .device-tab.unavailable { opacity: 0.4; }
      .signal-cti .device-tab.unavailable::after {
        content: ''; display: inline-block; width: 6px; height: 1px;
        background: currentColor; margin-left: 5px; opacity: 0.6;
      }
      .signal-cti .device-tabs .device-tab.active {
        background: var(--emerald); color: #fff; font-weight: 600;
        box-shadow:
          0 1px 0 rgba(255, 255, 255, 0.35) inset,
          0 -1px 0 rgba(0, 0, 0, 0.15) inset,
          0 1px 2px rgba(0, 0, 0, 0.12),
          0 3px 6px hsl(142, 71%, 36%, 0.32);
      }
      .signal-cti .device-tabs .device-tab.active:not(:disabled):hover {
        filter: brightness(1.05);
      }

      .signal-cti .device-spin {
        width: 12px; height: 12px; border-radius: 50%;
        border: 2px solid var(--text-dim);
        border-top-color: transparent;
        animation: device-spin 0.85s linear infinite;
      }
      @keyframes device-spin { to { transform: rotate(360deg); } }

      .signal-cti .kpi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .signal-cti .kpi { padding: 10px; border-radius: 8px; background: var(--surface-2); border: 1px solid var(--border); }
      .signal-cti .kpi-label { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); margin-bottom: 4px; }
      .signal-cti .kpi-value { font-size: 18px; font-weight: 600; letter-spacing: -0.02em; color: var(--text); }
      .signal-cti .kpi-delta { font-size: 10px; color: var(--text-dim); margin-top: 1px; }
      .signal-cti .kpi-delta.up { color: var(--emerald); }

      .signal-cti .broadcast {
        padding: 10px; border-radius: 8px; background: var(--amber-soft);
        border-left: 3px solid var(--amber); font-size: 12px; line-height: 1.4;
      }
      .signal-cti .broadcast-from {
        font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase;
        color: var(--amber); margin-bottom: 3px; font-weight: 600;
      }

      .signal-cti .sidebar-bottom {
        margin-top: auto; display: flex; flex-direction: column; gap: 6px;
        padding-top: 12px; border-top: 1px solid var(--border);
      }
      .signal-cti .conn-row { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--text-muted); }
      .signal-cti .signal-bars { display: inline-flex; align-items: flex-end; gap: 2px; }
      .signal-cti .signal-bar { width: 3px; background: var(--emerald); border-radius: 1px; }
      .signal-cti .signal-bar:nth-child(1) { height: 4px; }
      .signal-cti .signal-bar:nth-child(2) { height: 7px; }
      .signal-cti .signal-bar:nth-child(3) { height: 10px; }
      .signal-cti .signal-bar:nth-child(4) { height: 13px; }

      /* Main */
      .signal-cti .main { display: flex; flex-direction: column; min-width: 0; }
      .signal-cti .topbar {
        display: flex; align-items: center; gap: 12px;
        padding: 12px 20px; background: var(--surface); border-bottom: 1px solid var(--border);
        position: sticky; top: 0; z-index: 5;
      }
      .signal-cti .search {
        flex: 1; max-width: 420px; display: flex; align-items: center; gap: 8px;
        padding: 8px 12px; background: var(--surface-2); border: 1px solid var(--border);
        border-radius: 8px; color: var(--text-muted); font-size: 13px;
      }
      .signal-cti .search svg { opacity: 0.6; }
      .signal-cti .search-kbd {
        margin-left: auto; font-family: ui-monospace, monospace; font-size: 10px;
        padding: 2px 5px; background: var(--surface); border-radius: 4px;
        color: var(--text-dim); border: 1px solid var(--border);
      }

      .signal-cti .state-tabs {
        display: flex; gap: 4px; padding: 4px; background: var(--surface-2);
        border-radius: 8px; border: 1px solid var(--border);
      }
      .signal-cti .state-tab {
        padding: 5px 10px; font-size: 11px; font-weight: 500; letter-spacing: 0.02em;
        color: var(--text-muted); border-radius: 5px; cursor: pointer;
        display: flex; align-items: center; gap: 5px;
        transition: background 120ms, color 120ms;
        border: none; background: transparent; font-family: inherit;
      }
      .signal-cti .state-tab:hover { color: var(--text); }
      .signal-cti .state-tab.active { background: var(--surface); color: var(--text); box-shadow: var(--shadow-sm); }
      .signal-cti .state-tab .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; opacity: 0.6; }

      /* In-call view inside the dial popover */
      .signal-cti .popcall { display: flex; flex-direction: column; gap: 12px; }
      .signal-cti .popcall-caller {
        display: flex; align-items: center; gap: 10px;
        padding: 10px 12px; border-radius: 10px;
        background: var(--surface); box-shadow: var(--inset-shadow-sm);
      }
      .signal-cti .popcall-avatar {
        width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
        display: grid; place-items: center;
        background: linear-gradient(135deg, #f59e0b, #ec4899);
        color: #fff; font-weight: 600; font-size: 12px;
        letter-spacing: 0.03em;
      }
      .signal-cti .popcall-body { flex: 1; min-width: 0; }
      .signal-cti .popcall-name {
        font-size: 12px; font-weight: 600; color: var(--text);
        letter-spacing: 0.01em;
      }
      .signal-cti .popcall-num {
        font-size: 11px; color: var(--text-muted);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .signal-cti .popcall-timer {
        font-size: 15px; font-weight: 600;
        color: var(--emerald); flex-shrink: 0;
        display: inline-flex; align-items: center;
      }

      /* Control row */
      .signal-cti .popcall-ctrls {
        display: grid; grid-template-columns: repeat(5, 40px) 1fr; gap: 6px;
        align-items: center;
      }
      .signal-cti .popcall-btn {
        width: 40px; height: 40px;
        display: grid; place-items: center;
        border: none; border-radius: 10px;
        background: var(--surface); color: var(--text-muted);
        box-shadow: var(--raise-shadow-sm);
        cursor: pointer; font-family: inherit;
        transition: color 120ms, box-shadow 140ms;
      }
      .signal-cti .popcall-btn:not(:disabled):hover { color: var(--text); }
      .signal-cti .popcall-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .signal-cti .popcall-btn.on {
        color: var(--amber); box-shadow: var(--inset-shadow-sm);
      }
      .signal-cti .popcall-btn.end {
        width: auto; height: 40px;
        display: inline-flex; align-items: center; justify-content: center; gap: 6px;
        padding: 0 12px;
        background: var(--rose); color: #fff;
        font-weight: 600; font-size: 13px;
        box-shadow: 0 4px 12px hsl(0, 72%, 51%, 0.35);
      }
      .signal-cti .popcall-btn.end:hover { filter: brightness(1.05); }

      /* Sub-panel (keypad / transfer / conference) inside in-call */
      .signal-cti .popcall-sub {
        display: flex; flex-direction: column; gap: 10px;
        padding: 12px; border-radius: 10px;
        background: var(--surface); box-shadow: var(--inset-shadow-sm);
      }
      .signal-cti .popcall-sub-head {
        font-size: 10px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 0.08em; color: var(--text-dim);
      }
      .signal-cti .popcall-dtmf {
        padding: 8px 12px; border-radius: 8px;
        background: var(--surface); box-shadow: var(--inset-shadow-sm);
        font-size: 15px; color: var(--text); letter-spacing: 0.05em;
        min-height: 38px; display: flex; align-items: center;
      }

      .signal-cti .popcall-agents { display: flex; flex-direction: column; gap: 4px; max-height: 220px; overflow-y: auto; }
      .signal-cti .popcall-agent {
        display: flex; align-items: center; gap: 10px;
        padding: 6px 4px; border-radius: 6px;
      }
      .signal-cti .popcall-agent:hover { background: var(--surface-2); }
      .signal-cti .popcall-agent-dot {
        width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
        background: var(--emerald);
      }
      .signal-cti .popcall-agent-dot.busy { background: var(--amber); }
      .signal-cti .popcall-agent-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
      .signal-cti .popcall-agent-name {
        font-size: 12px; font-weight: 500; color: var(--text);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .signal-cti .popcall-agent-ext { font-size: 10px; color: var(--text-dim); }
      .signal-cti .popcall-agent-btns { display: flex; gap: 4px; flex-shrink: 0; }

      /* Outbound dialer — button + popover */
      .signal-cti .dial-wrap { position: relative; display: inline-block; }
      .signal-cti .dial-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 6px 12px; border: none; border-radius: 8px;
        background: var(--surface); color: var(--text);
        box-shadow: var(--raise-shadow-sm);
        font-size: 12px; font-weight: 600; font-family: inherit;
        cursor: pointer; transition: box-shadow 140ms, color 120ms;
      }
      .signal-cti .dial-btn:hover { color: var(--accent); }
      .signal-cti .dial-btn.active {
        color: var(--emerald); box-shadow: var(--inset-shadow-sm);
      }
      .signal-cti .dial-scrim {
        position: fixed; inset: 0; z-index: 40;
        background: hsl(0, 0%, 0%, 0.20);
      }
      .signal-cti .dial-pop {
        position: absolute; top: calc(100% + 10px); right: 0;
        width: 320px; z-index: 41;
        background: var(--surface); color: var(--text);
        border-radius: 14px; box-shadow: var(--raise-shadow);
        padding: 14px;
        display: flex; flex-direction: column; gap: 12px;
        animation: dial-pop-in 140ms ease-out;
        transition: width 160ms ease;
      }
      /* Widen when the recent panel is toggled in. */
      .signal-cti .dial-pop.has-recent { width: 560px; }
      /* Single-column body by default; add a right column when recent is on. */
      .signal-cti .dial-pop-body {
        display: grid; grid-template-columns: 1fr; gap: 14px;
        align-items: stretch;
      }
      .signal-cti .dial-pop.has-recent .dial-pop-body {
        grid-template-columns: 1fr 220px;
      }
      .signal-cti .dial-pop-main {
        display: flex; flex-direction: column; gap: 12px; min-width: 0;
      }
      .signal-cti .dial-pop-side {
        display: flex; flex-direction: column; gap: 8px;
        padding-left: 14px; border-left: 1px solid var(--border-strong);
        min-width: 0;
      }
      /* Floating mode — the user grabbed the handle and moved it. Switch
       * off the topbar anchor and pin to the viewport at (top, left) set
       * by the drag handler. Raise z-index so it also floats above the
       * on-call HUD. */
      .signal-cti .dial-pop.floating {
        position: fixed; z-index: 50;
        animation: none;
        box-shadow: var(--raise-shadow);
      }
      @keyframes dial-pop-in {
        from { opacity: 0; transform: translateY(-6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .signal-cti .dial-pop-head {
        display: flex; align-items: center; justify-content: space-between;
        gap: 8px;
        font-size: 11px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 0.08em; color: var(--text-dim);
      }
      .signal-cti .dial-drag-handle {
        cursor: grab; user-select: none;
      }
      .signal-cti .dial-drag-handle:active { cursor: grabbing; }
      /* Grip dots — visual affordance that the header is draggable. */
      .signal-cti .dial-drag-grip {
        display: grid; grid-template-columns: repeat(2, 3px); gap: 2px;
        margin-right: 4px;
      }
      .signal-cti .dial-drag-grip span {
        width: 3px; height: 3px; border-radius: 50%;
        background: var(--text-dim); opacity: 0.55;
      }
      .signal-cti .dial-drag-handle:hover .dial-drag-grip span { opacity: 0.9; }
      .signal-cti .dial-pop-title { flex: 1; }
      .signal-cti .dial-recent-toggle {
        display: inline-flex; align-items: center; gap: 5px;
        padding: 4px 8px; border: none; border-radius: 6px;
        background: var(--surface); color: var(--text-muted);
        box-shadow: var(--raise-shadow-sm);
        font-family: inherit; font-size: 10px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.06em;
        cursor: pointer;
        transition: color 120ms, box-shadow 140ms;
      }
      .signal-cti .dial-recent-toggle:hover { color: var(--text); }
      .signal-cti .dial-recent-toggle.on {
        color: var(--accent); box-shadow: var(--inset-shadow-sm);
      }
      .signal-cti .dial-caret {
        font-size: 13px; line-height: 1; font-weight: 700;
        transition: transform 160ms ease;
      }
      .signal-cti .dial-caret.open { transform: rotate(180deg); }

      .signal-cti .dial-pop-close {
        display: grid; place-items: center;
        width: 22px; height: 22px; border: none; border-radius: 6px;
        background: transparent; color: var(--text-dim);
        cursor: pointer; transition: color 120ms;
      }
      .signal-cti .dial-pop-close:hover { color: var(--text); }

      /* Number display — relative anchor for the absolutely-positioned
       * backspace button. Reserved right padding so the input's text can't
       * slide under the button. */
      .signal-cti .dial-display {
        position: relative;
        display: block;
        padding: 10px 44px 10px 12px;
        border: none; border-radius: 10px;
        background: var(--surface); box-shadow: var(--inset-shadow-sm);
        min-height: 44px;
      }
      .signal-cti .dial-display input {
        display: block; width: 100%;
        border: none; outline: none; background: transparent;
        color: var(--text); font-family: ui-monospace, monospace;
        font-variant-numeric: tabular-nums; font-size: 16px; letter-spacing: 0.02em;
        padding: 0; margin: 0;
      }
      .signal-cti .dial-display input::placeholder { color: var(--text-dim); }
      /* Backspace — hard-pinned to the right edge of the display so it
       * can never drift into the keypad. */
      .signal-cti .dial-clear {
        position: absolute; top: 50%; right: 8px; transform: translateY(-50%);
        display: grid; place-items: center;
        width: 28px; height: 28px;
        border: none; border-radius: 7px;
        background: var(--surface); color: var(--text-dim);
        box-shadow: var(--raise-shadow-sm);
        cursor: pointer; padding: 0;
        transition: color 120ms, box-shadow 140ms;
      }
      .signal-cti .dial-clear:hover { color: var(--rose); }
      .signal-cti .dial-clear:active { box-shadow: var(--inset-shadow-sm); }

      .signal-cti .dial-keypad {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
      }
      .signal-cti .dial-key {
        aspect-ratio: 1 / 1;
        display: grid; place-items: center;
        padding: 0; border: none; border-radius: 12px;
        background: var(--surface); color: var(--text);
        font-size: 16px; font-weight: 600; font-family: inherit;
        box-shadow: var(--raise-shadow-sm);
        cursor: pointer;
        transition: box-shadow 140ms, transform 60ms, color 120ms;
      }
      .signal-cti .dial-key:hover { color: var(--accent); }
      .signal-cti .dial-key:active {
        color: var(--accent);
        box-shadow: var(--inset-shadow-sm);
        transform: translateY(1px);
      }

      .signal-cti .dial-call-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        height: 44px; border: none; border-radius: 10px;
        background: var(--emerald); color: #fff;
        font-size: 13px; font-weight: 600; font-family: inherit;
        letter-spacing: -0.01em; cursor: pointer;
        box-shadow: 0 6px 18px hsl(142, 71%, 36%, 0.30);
        transition: filter 120ms;
      }
      .signal-cti .dial-call-btn:hover:not(:disabled) { filter: brightness(1.05); }
      .signal-cti .dial-call-btn:disabled {
        opacity: 0.4; cursor: not-allowed;
        box-shadow: var(--raise-shadow-sm);
        background: var(--surface); color: var(--text-dim);
      }

      .signal-cti .dial-hint {
        padding: 8px 10px; border-radius: 8px;
        background: var(--amber-soft); color: var(--amber);
        font-size: 11px; line-height: 1.4;
      }

      .signal-cti .dial-recent-head {
        font-size: 11px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 0.08em; color: var(--text-dim);
      }
      .signal-cti .dial-recent {
        display: flex; flex-direction: column; gap: 2px;
        flex: 1; min-height: 0; overflow-y: auto;
        max-height: 300px;
      }
      .signal-cti .dial-recent-row {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 4px; border: none; background: transparent;
        text-align: left; cursor: pointer; color: var(--text);
        border-radius: 8px; transition: background 120ms;
        font-family: inherit;
      }
      .signal-cti .dial-recent-row:hover { background: var(--surface-2); }
      .signal-cti .dial-recent-body { flex: 1; min-width: 0; }
      .signal-cti .dial-recent-name {
        font-size: 12px; font-weight: 500;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .signal-cti .dial-recent-num { font-size: 10px; color: var(--text-dim); }
      .signal-cti .dial-recent-at {
        font-size: 10px; color: var(--text-dim); flex-shrink: 0;
      }

      .signal-cti .theme-toggle-seg {
        margin-left: auto;
        display: inline-flex; gap: 2px; padding: 2px;
        border-radius: 8px; border: 1px solid var(--border); background: var(--surface-2);
      }
      .signal-cti .theme-seg-btn {
        display: grid; place-items: center;
        width: 28px; height: 26px; border: none; border-radius: 6px;
        background: transparent; color: var(--text-muted); font-family: inherit;
        cursor: pointer; transition: all 120ms;
      }
      .signal-cti .theme-seg-btn:hover { color: var(--text); }
      .signal-cti .theme-seg-btn.active {
        background: var(--surface); color: var(--text); box-shadow: var(--shadow-sm);
      }

      /* Canvas */
      .signal-cti .canvas { flex: 1; padding: 24px; background: var(--ground); }
      .signal-cti .view { display: none; }
      .signal-cti .view.active { display: block; }

      /* Idle */
      .signal-cti .idle-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
      /* Raised surfaces — same tone as the ground, lifted by the two-tone
       * neumorphic shadow. */
      .signal-cti .sc-card,
      .signal-cti .customer-card,
      .signal-cti .wrap-main,
      .signal-cti .wrap-metric,
      .signal-cti .script,
      .signal-cti .disposition,
      .signal-cti .ai-assist,
      .signal-cti .sentiment-strip,
      .signal-cti .ringing-hero,
      .signal-cti .break-hero,
      .signal-cti .state-panel,
      .signal-cti .kpi,
      .signal-cti .broadcast {
        background: var(--surface); color: var(--text);
        border: 1px solid var(--border); border-radius: 14px;
        box-shadow: var(--raise-shadow);
      }
      .signal-cti .sc-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; }

      /* Inset (pressed-in) surfaces — nested inside a raised card. */
      .signal-cti .queue-item,
      .signal-cti .info-grid,
      .signal-cti .customer-stats,
      .signal-cti .wrap-summary,
      .signal-cti .history-item,
      .signal-cti .dispo-input,
      .signal-cti .keypad-display,
      .signal-cti .search {
        background: var(--surface); color: var(--text);
        border: 1px solid var(--border); border-radius: 10px;
        box-shadow: var(--inset-shadow-sm);
      }
      .signal-cti .card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
      .signal-cti .card-title { font-size: 13px; font-weight: 600; letter-spacing: -0.01em; color: var(--text); }
      .signal-cti .card-count {
        padding: 2px 7px; background: var(--accent-soft); color: var(--accent);
        border-radius: 999px; font-size: 10px; font-weight: 600; letter-spacing: 0.04em;
      }
      .signal-cti .queue-item {
        padding: 10px; border-radius: 8px; display: flex; align-items: center; gap: 10px;
        background: var(--surface-2); border: 1px solid transparent; cursor: pointer;
        transition: border-color 120ms;
      }
      .signal-cti .queue-item:hover { border-color: var(--accent-ring); }
      .signal-cti .queue-avatar {
        width: 32px; height: 32px; border-radius: 50%;
        background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff;
        display: grid; place-items: center; font-weight: 600; font-size: 12px; flex-shrink: 0;
      }
      .signal-cti .queue-avatar.q2 { background: linear-gradient(135deg, #06b6d4, #3b82f6); }
      .signal-cti .queue-avatar.q3 { background: linear-gradient(135deg, #f97316, #ef4444); }
      .signal-cti .queue-avatar.q4 { background: linear-gradient(135deg, #10b981, #059669); }
      .signal-cti .queue-meta { flex: 1; min-width: 0; }
      .signal-cti .queue-name { font-size: 13px; font-weight: 500; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .signal-cti .queue-reason { font-size: 11px; color: var(--text-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .signal-cti .queue-time { font-size: 11px; color: var(--text-dim); flex-shrink: 0; }
      .signal-cti .queue-priority { width: 3px; align-self: stretch; border-radius: 2px; background: var(--rose); flex-shrink: 0; }
      .signal-cti .queue-priority.medium { background: var(--amber); }
      .signal-cti .queue-priority.low    { background: var(--text-dim); opacity: 0.4; }

      .signal-cti .ready-toggle {
        display: flex; align-items: center; justify-content: space-between;
        padding: 20px 20px; border-radius: 12px; margin-bottom: 16px;
        border: 1px solid var(--emerald-soft);
        background: linear-gradient(135deg, var(--emerald-soft), var(--accent-soft));
        position: relative; overflow: hidden;
      }
      .signal-cti .ready-toggle::before {
        content: ""; position: absolute; inset: 0;
        background:
          radial-gradient(circle at 20% 50%, var(--emerald-soft), transparent 60%),
          radial-gradient(circle at 80% 50%, var(--accent-soft), transparent 60%);
        opacity: 0.6; pointer-events: none;
      }
      .signal-cti .ready-info { display: flex; flex-direction: column; gap: 4px; position: relative; }
      .signal-cti .ready-title { font-size: 20px; font-weight: 700; letter-spacing: -0.02em; }
      .signal-cti .ready-sub { font-size: 12px; color: var(--text-muted); }
      .signal-cti .ready-btn {
        padding: 10px 18px; background: var(--emerald); color: #0F1720; border: none;
        border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer;
        display: flex; align-items: center; gap: 6px;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        position: relative; font-family: inherit;
      }

      /* Ringing */
      .signal-cti .ringing-hero {
        display: grid; grid-template-columns: 320px 1fr; gap: 24px;
        padding: 32px; background: var(--surface); border: 1px solid var(--border);
        border-radius: 16px; min-height: 460px; position: relative; overflow: hidden;
      }
      .signal-cti .ringing-hero::before {
        content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px;
        background: var(--sky); animation: signal-rail 2.4s infinite ease-in-out;
      }
      @keyframes signal-rail {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
      .signal-cti .caller-visual {
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        text-align: center; position: relative;
      }
      .signal-cti .sonar { position: relative; width: 200px; height: 200px; display: grid; place-items: center; margin-bottom: 20px; }
      .signal-cti .sonar-ring {
        position: absolute; inset: 0; border-radius: 50%;
        border: 2px solid var(--sky); opacity: 0;
        animation: signal-sonar 2.4s infinite ease-out;
      }
      .signal-cti .sonar-ring:nth-child(2) { animation-delay: 0.6s; }
      .signal-cti .sonar-ring:nth-child(3) { animation-delay: 1.2s; }
      @keyframes signal-sonar {
        0% { transform: scale(0.6); opacity: 0.9; }
        100% { transform: scale(1.4); opacity: 0; }
      }
      .signal-cti .caller-avatar {
        width: 120px; height: 120px; border-radius: 50%;
        background: linear-gradient(135deg, #f59e0b, #ec4899);
        color: #fff; display: grid; place-items: center;
        font-weight: 600; font-size: 36px; letter-spacing: -0.02em;
        position: relative; z-index: 2;
        box-shadow: 0 12px 40px rgba(37, 99, 235, 0.35);
      }
      .signal-cti .caller-name { font-size: 22px; font-weight: 700; letter-spacing: -0.02em; margin-top: 6px; }
      .signal-cti .caller-num { font-size: 14px; color: var(--text-muted); margin-top: 2px; }

      .signal-cti .caller-info { display: flex; flex-direction: column; gap: 16px; }
      .signal-cti .badge-row { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 4px; }
      .signal-cti .badge {
        padding: 4px 9px; border-radius: 999px; font-size: 11px; font-weight: 600;
        letter-spacing: 0.02em; display: inline-flex; align-items: center; gap: 4px;
      }
      .signal-cti .badge.priority { background: var(--rose-soft); color: var(--rose); }
      .signal-cti .badge.repeat   { background: var(--sky-soft); color: var(--sky); }
      .signal-cti .badge.tier     { background: var(--accent-soft); color: var(--accent); }
      .signal-cti .badge.campaign { background: var(--amber-soft); color: var(--amber); }

      .signal-cti .info-grid {
        display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
        padding: 14px; background: var(--surface-2); border-radius: 10px; border: 1px solid var(--border);
      }
      .signal-cti .info-cell { display: flex; flex-direction: column; gap: 3px; }
      .signal-cti .info-label { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; }
      .signal-cti .info-value { font-size: 13px; color: var(--text); }

      .signal-cti .prefetch-title {
        font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
        color: var(--text-dim); font-weight: 600;
        display: flex; align-items: center; gap: 6px;
      }
      .signal-cti .prefetch-title::before {
        content: ""; width: 4px; height: 4px; border-radius: 50%; background: var(--accent);
      }
      .signal-cti .history-list { display: flex; flex-direction: column; gap: 8px; }
      .signal-cti .history-item {
        display: flex; gap: 10px; padding: 10px 12px; border-radius: 8px;
        background: var(--surface-2); border: 1px solid var(--border);
      }
      .signal-cti .history-icon {
        width: 28px; height: 28px; border-radius: 50%;
        background: var(--accent-soft); color: var(--accent);
        display: grid; place-items: center; flex-shrink: 0;
      }
      .signal-cti .history-body { flex: 1; min-width: 0; }
      .signal-cti .history-head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
      .signal-cti .history-what { font-size: 12px; font-weight: 500; }
      .signal-cti .history-when { font-size: 11px; color: var(--text-dim); flex-shrink: 0; }
      .signal-cti .history-detail { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

      .signal-cti .ringing-actions {
        display: flex; gap: 12px; justify-content: center;
        margin-top: 24px; padding-top: 24px; border-top: 1px solid var(--border);
      }
      /* Ringing action buttons — !important on box-model props because
       * something in the AppShell / Tailwind base was squishing them to
       * a ~90px square regardless of my class rules. */
      .signal-cti .ring-btn {
        display: inline-flex !important; align-items: center !important;
        justify-content: center !important; gap: 8px !important;
        padding: 12px 24px !important;
        border: 1px solid transparent !important; border-radius: 10px !important;
        font-weight: 600 !important; font-size: 14px !important;
        line-height: 1 !important; letter-spacing: -0.01em !important;
        font-family: inherit !important;
        white-space: nowrap !important;
        width: auto !important; height: auto !important;
        min-width: 120px !important; min-height: 44px !important;
        cursor: pointer !important;
      }
      .signal-cti .ring-btn.answer {
        background: var(--emerald) !important; color: #fff !important;
        box-shadow: 0 6px 18px hsl(142, 71%, 36%, 0.35) !important;
        padding: 14px 32px !important; min-width: 148px !important; min-height: 48px !important;
      }
      .signal-cti[data-theme="dark"] .ring-btn.answer { color: hsl(222, 47%, 8%) !important; }
      .signal-cti .ring-btn.reject {
        background: transparent !important; color: var(--rose) !important;
        border-color: var(--rose) !important;
      }
      /* Flex-children — the icon SVG and the kbd chip must not shrink so the
       * button's inner row stays intact when any parent tries to constrain. */
      .signal-cti .ring-btn > svg { flex-shrink: 0; }
      .signal-cti .ring-btn .kbd {
        display: inline-block; padding: 2px 6px;
        background: hsl(0, 0%, 0%, 0.15); border-radius: 4px;
        font-family: ui-monospace, monospace; font-size: 10px; font-weight: 500;
        margin-left: 4px; color: inherit; flex-shrink: 0;
      }
      .signal-cti .ring-btn.answer .kbd { background: hsl(0, 0%, 0%, 0.20); }

      /* On call */
      .signal-cti .oncall-grid { display: grid; grid-template-columns: 3fr 2fr; gap: 16px; padding-bottom: 68px; }
      .signal-cti .sentiment-strip {
        display: flex; align-items: center; gap: 12px;
        padding: 10px 14px; margin-bottom: 16px;
        background: var(--surface); border: 1px solid var(--border); border-radius: 10px;
      }
      .signal-cti .sentiment-label { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; }
      .signal-cti .sentiment-bar {
        flex: 1; height: 6px; border-radius: 3px;
        background: linear-gradient(90deg, var(--rose) 0%, var(--amber) 45%, var(--emerald) 100%);
        position: relative;
      }
      .signal-cti .sentiment-marker {
        position: absolute; top: -3px; left: 78%;
        width: 3px; height: 12px; background: var(--text); border-radius: 2px;
      }
      .signal-cti .sentiment-value { font-size: 12px; color: var(--emerald); font-weight: 600; }

      .signal-cti .oncall-left { display: flex; flex-direction: column; gap: 16px; }
      .signal-cti .script { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
      .signal-cti .script-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
      .signal-cti .script-progress { display: flex; gap: 3px; }
      .signal-cti .script-step { width: 24px; height: 3px; border-radius: 2px; background: var(--border); }
      .signal-cti .script-step.done { background: var(--accent); }
      .signal-cti .script-step.active { background: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
      .signal-cti .script-body { font-size: 14px; line-height: 1.65; color: var(--text); }
      .signal-cti .script-body p { margin: 0 0 12px 0; }
      .signal-cti .script-highlight { background: var(--amber-soft); padding: 1px 4px; border-radius: 3px; color: var(--amber); font-weight: 500; }
      .signal-cti .script-check {
        display: flex; align-items: center; gap: 8px;
        padding: 10px 12px; margin-top: 12px;
        background: var(--emerald-soft); border-radius: 8px;
        border-left: 3px solid var(--emerald);
        font-size: 12px; color: var(--text);
      }
      .signal-cti .script-check svg { color: var(--emerald); }

      .signal-cti .disposition { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
      .signal-cti .disposition-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
      .signal-cti .disposition-title { font-size: 13px; font-weight: 600; }
      .signal-cti .ai-badge {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 3px 8px; background: var(--accent-soft); color: var(--accent);
        border-radius: 999px; font-size: 10px; font-weight: 600; letter-spacing: 0.02em;
      }
      .signal-cti .dispo-row {
        display: grid; grid-template-columns: 100px 1fr; gap: 12px;
        align-items: center; padding: 8px 0; border-bottom: 1px solid var(--border);
      }
      .signal-cti .dispo-row:last-child { border-bottom: none; }
      .signal-cti .dispo-label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; }
      .signal-cti .dispo-chips { display: flex; flex-wrap: wrap; gap: 6px; }
      .signal-cti .chip {
        padding: 4px 10px; border-radius: 999px; font-size: 12px;
        background: var(--surface-2); border: 1px solid var(--border);
        color: var(--text-muted); cursor: pointer;
        display: inline-flex; align-items: center; gap: 4px;
      }
      .signal-cti .chip.selected {
        background: var(--accent-soft); color: var(--accent);
        border-color: var(--accent-ring); font-weight: 500;
      }
      .signal-cti .chip.ai-suggest { border-style: dashed; color: var(--accent); border-color: var(--accent-ring); }
      .signal-cti .dispo-input {
        background: var(--surface-2); border: 1px solid var(--border);
        border-radius: 8px; padding: 8px 12px; color: var(--text);
        font-family: inherit; font-size: 13px;
      }

      .signal-cti .oncall-right { display: flex; flex-direction: column; gap: 16px; }
      .signal-cti .customer-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
      .signal-cti .customer-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
      .signal-cti .customer-info { flex: 1; }
      .signal-cti .customer-name { font-size: 15px; font-weight: 600; letter-spacing: -0.01em; }
      .signal-cti .customer-tier { font-size: 11px; color: var(--accent); font-weight: 500; margin-top: 1px; }
      .signal-cti .customer-stats {
        display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
        padding: 12px; background: var(--surface-2); border-radius: 8px; margin-bottom: 16px;
      }
      .signal-cti .stat-cell { display: flex; flex-direction: column; gap: 2px; }
      .signal-cti .stat-label { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; }
      .signal-cti .stat-value { font-size: 13px; font-weight: 500; }

      .signal-cti .timeline { display: flex; flex-direction: column; gap: 12px; }
      .signal-cti .timeline-item { display: flex; gap: 10px; position: relative; }
      .signal-cti .timeline-icon {
        width: 24px; height: 24px; border-radius: 50%;
        background: var(--surface-2); display: grid; place-items: center;
        color: var(--text-muted); flex-shrink: 0;
        position: relative; z-index: 2; border: 2px solid var(--surface);
      }
      .signal-cti .timeline-item:not(:last-child)::after {
        content: ""; position: absolute; left: 11px; top: 24px;
        width: 2px; height: calc(100% + 12px); background: var(--border);
      }
      .signal-cti .timeline-body { flex: 1; padding-top: 3px; }
      .signal-cti .timeline-what { font-size: 12px; font-weight: 500; }
      .signal-cti .timeline-when { font-size: 10px; color: var(--text-dim); margin-top: 1px; }

      .signal-cti .ai-assist {
        background: linear-gradient(135deg, var(--accent-soft), transparent);
        border: 1px solid var(--accent-ring); border-radius: 12px; padding: 14px;
      }
      .signal-cti .ai-head {
        display: flex; align-items: center; gap: 6px; margin-bottom: 8px;
        font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
        color: var(--accent); font-weight: 600;
      }
      .signal-cti .ai-nudge { font-size: 12px; line-height: 1.5; color: var(--text); }
      .signal-cti .ai-nudge b { color: var(--accent); }

      /* HUD */
      .signal-cti .call-hud {
        position: fixed; bottom: 0; left: 240px; right: 0;
        padding: 10px 20px; background: var(--surface); border-top: 1px solid var(--border);
        display: flex; align-items: center; gap: 16px; z-index: 10;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
      }
      .signal-cti .hud-caller { display: flex; align-items: center; gap: 10px; min-width: 220px; }
      .signal-cti .hud-avatar {
        width: 32px; height: 32px; border-radius: 50%;
        background: linear-gradient(135deg, #f59e0b, #ec4899);
        color: #fff; display: grid; place-items: center;
        font-weight: 600; font-size: 12px; position: relative;
      }
      .signal-cti .hud-avatar::after {
        content: ""; position: absolute; right: -2px; bottom: -2px;
        width: 10px; height: 10px; border-radius: 50%;
        background: var(--emerald); border: 2px solid var(--surface);
        animation: signal-pulse 1.4s infinite ease-out;
      }
      @keyframes signal-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      .signal-cti .hud-name { font-size: 13px; font-weight: 500; }
      .signal-cti .hud-timer { font-size: 12px; color: var(--emerald); font-weight: 600; }

      .signal-cti .hud-controls { display: flex; gap: 6px; flex: 1; justify-content: center; }
      .signal-cti .hud-btn {
        width: 40px; height: 40px; border-radius: 10px;
        background: var(--surface-2); border: 1px solid var(--border);
        color: var(--text-muted); cursor: pointer;
        display: grid; place-items: center;
        transition: color 120ms; font-family: inherit;
      }
      .signal-cti .hud-btn:hover { color: var(--text); }
      .signal-cti .hud-btn.active { background: var(--amber-soft); color: var(--amber); border-color: var(--amber); }
      .signal-cti .hud-btn.end {
        background: var(--rose); color: #fff; border: none;
        width: 100px; font-weight: 600; font-size: 13px; gap: 6px;
        display: flex; align-items: center; justify-content: center; padding: 0 16px;
      }
      .signal-cti .hud-btn.end:hover { color: #fff; opacity: 0.9; }
      .signal-cti .hud-quality {
        display: flex; align-items: center; gap: 6px;
        font-size: 11px; color: var(--text-muted);
        min-width: 120px; justify-content: flex-end;
      }

      /* HUD popovers (transfer / conference / keypad) */
      .signal-cti .hud-scrim {
        position: fixed; inset: 0; z-index: 8;
        background: hsl(0, 0%, 0%, 0.20);
      }
      .signal-cti .hud-btn-wrap { position: relative; display: inline-block; }
      .signal-cti .hud-pop {
        position: absolute; bottom: calc(100% + 10px); left: 50%; transform: translateX(-50%);
        min-width: 280px; max-width: 340px;
        background: var(--surface); border: 1px solid var(--border);
        border-radius: 12px; box-shadow: var(--shadow-md);
        z-index: 11; overflow: hidden;
        animation: hud-pop-in 140ms ease-out;
      }
      @keyframes hud-pop-in {
        from { opacity: 0; transform: translateX(-50%) translateY(6px); }
        to   { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
      .signal-cti .hud-pop-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 10px 14px; border-bottom: 1px solid var(--border);
        font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
        color: var(--text-dim); font-weight: 600;
      }
      .signal-cti .hud-pop-list { max-height: 260px; overflow-y: auto; padding: 4px 0; }
      .signal-cti .hud-pop-row {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 14px; transition: background 120ms;
      }
      .signal-cti .hud-pop-row:hover { background: var(--surface-2); }
      .signal-cti .hud-pop-dot {
        width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
        background: var(--emerald);
      }
      .signal-cti .hud-pop-dot.busy   { background: var(--amber); }
      .signal-cti .hud-pop-body { flex: 1; min-width: 0; }
      .signal-cti .hud-pop-name {
        font-size: 12px; font-weight: 500; color: var(--text);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .signal-cti .hud-pop-sub {
        font-size: 10px; color: var(--text-dim);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .signal-cti .hud-pop-btns { display: flex; gap: 4px; flex-shrink: 0; }
      .signal-cti .hud-pop-btn {
        padding: 4px 10px; border: 1px solid var(--border); border-radius: 6px;
        background: var(--surface-2); color: var(--text-muted);
        font-size: 10px; font-weight: 600; font-family: inherit; cursor: pointer;
        transition: color 120ms;
      }
      .signal-cti .hud-pop-btn:not(:disabled):hover { color: var(--text); }
      .signal-cti .hud-pop-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .signal-cti .hud-pop-btn.primary {
        background: var(--accent-soft); color: var(--accent);
        border-color: var(--accent-ring);
      }
      .signal-cti .hud-pop-btn.primary:hover { opacity: 0.85; }

      /* Keypad popover */
      .signal-cti .keypad-pop { min-width: 240px; max-width: 240px; }
      .signal-cti .keypad-clear {
        border: none; background: transparent; color: var(--rose);
        font-family: inherit; font-size: 10px; font-weight: 600;
        letter-spacing: 0.06em; cursor: pointer; text-transform: uppercase;
      }
      .signal-cti .keypad-display {
        margin: 12px 14px 0; padding: 10px 14px;
        background: var(--surface); color: var(--text);
        border: none; border-radius: 10px;
        box-shadow: var(--inset-shadow-sm);
        font-size: 15px; letter-spacing: 0.04em;
        min-height: 42px; display: flex; align-items: center;
      }
      .signal-cti .keypad-display:empty::before { content: 'Press a key'; color: var(--text-dim); }

      .signal-cti .keypad-grid {
        display: grid; grid-template-columns: repeat(3, 1fr);
        gap: 10px; padding: 14px;
      }
      /* Neumorphic keypad keys — same tone as the popover, lifted by a soft
       * two-tone shadow; deepen on hover; press down with an inset shadow +
       * 1px nudge on :active. */
      .signal-cti .keypad-key {
        aspect-ratio: 1 / 1;
        display: grid; place-items: center;
        padding: 0; border: none; border-radius: 12px;
        background: var(--surface); color: var(--text);
        font-size: 17px; font-weight: 600; font-family: inherit;
        box-shadow: var(--raise-shadow-sm);
        cursor: pointer;
        transition: box-shadow 140ms ease, transform 60ms ease, color 120ms ease;
      }
      .signal-cti .keypad-key:hover {
        color: var(--accent);
        box-shadow:
          6px 6px 14px rgba(163, 177, 198, 0.6),
          -6px -6px 14px rgba(255, 255, 255, 0.95);
      }
      .signal-cti[data-theme="dark"] .keypad-key:hover {
        box-shadow:
          6px 6px 14px rgba(0, 0, 0, 0.55),
          -6px -6px 14px rgba(255, 255, 255, 0.03);
      }
      .signal-cti .keypad-key:active {
        color: var(--accent);
        box-shadow: var(--inset-shadow-sm);
        transform: translateY(1px);
      }

      /* Wrap */
      .signal-cti .wrap-hero { display: grid; grid-template-columns: 1fr 320px; gap: 20px; }
      .signal-cti .wrap-main { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 28px; }
      .signal-cti .wrap-header {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--border);
      }
      .signal-cti .wrap-caller { display: flex; align-items: center; gap: 12px; }
      .signal-cti .wrap-caller-name { font-size: 16px; font-weight: 600; }
      .signal-cti .wrap-caller-meta { font-size: 11px; color: var(--text-dim); margin-top: 1px; display: flex; gap: 10px; }
      .signal-cti .wrap-timer { text-align: right; }
      .signal-cti .wrap-timer-num { font-size: 22px; font-weight: 700; letter-spacing: -0.02em; color: var(--amber); }
      .signal-cti .wrap-timer-label { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; margin-top: 1px; }

      .signal-cti .wrap-summary {
        background: var(--surface-2); border-radius: 10px;
        padding: 16px; margin-bottom: 16px; border: 1px solid var(--border);
      }
      .signal-cti .wrap-summary-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
      .signal-cti .wrap-summary-title { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; }
      .signal-cti .wrap-summary-body { font-size: 13px; line-height: 1.6; color: var(--text); }

      .signal-cti .wrap-actions { display: flex; gap: 10px; justify-content: flex-end; padding-top: 16px; border-top: 1px solid var(--border); }
      .signal-cti .wrap-btn { padding: 10px 18px; border-radius: 8px; font-weight: 500; font-size: 13px; cursor: pointer; border: none; font-family: inherit; }
      .signal-cti .wrap-btn.ghost { background: transparent; color: var(--text-muted); border: 1px solid var(--border); }
      .signal-cti .wrap-btn.primary { background: var(--accent); color: var(--ground); font-weight: 600; display: flex; align-items: center; gap: 6px; }

      .signal-cti .wrap-side { display: flex; flex-direction: column; gap: 16px; }
      .signal-cti .wrap-metric { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 16px; }
      .signal-cti .wrap-metric-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
      .signal-cti .wrap-metric-title { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; }
      .signal-cti .wrap-mini-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .signal-cti .wrap-mini { display: flex; flex-direction: column; gap: 2px; }
      .signal-cti .wrap-mini-value { font-size: 18px; font-weight: 600; letter-spacing: -0.02em; }
      .signal-cti .wrap-mini-label { font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; }

      /* Break */
      .signal-cti .break-hero {
        display: flex; flex-direction: column; align-items: center; text-align: center;
        padding: 60px 24px; background: var(--surface); border: 1px solid var(--border);
        border-radius: 16px; position: relative; overflow: hidden;
      }
      .signal-cti .break-hero::before {
        content: ""; position: absolute; top: -100px; left: 50%; transform: translateX(-50%);
        width: 400px; height: 400px; border-radius: 50%;
        background: radial-gradient(circle, var(--rose-soft), transparent 70%);
        pointer-events: none;
      }
      .signal-cti .break-title { font-size: 32px; font-weight: 700; letter-spacing: -0.03em; position: relative; margin-bottom: 4px; }
      .signal-cti .break-sub { color: var(--text-muted); font-size: 14px; position: relative; margin-bottom: 30px; }
      .signal-cti .break-clock { font-size: 72px; font-weight: 700; letter-spacing: -0.05em; line-height: 1; color: var(--rose); position: relative; margin-bottom: 8px; }
      .signal-cti .break-elapsed { font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); font-weight: 600; position: relative; margin-bottom: 30px; }
      .signal-cti .break-actions { display: flex; gap: 12px; position: relative; }
      .signal-cti .break-btn {
        padding: 12px 24px; border-radius: 10px; font-weight: 600; font-size: 14px;
        cursor: pointer; border: none; letter-spacing: -0.01em; font-family: inherit;
        display: flex; align-items: center; gap: 8px;
      }
      .signal-cti .break-btn.resume {
        background: var(--emerald); color: #0F1720;
        box-shadow: 0 6px 18px rgba(16, 185, 129, 0.35);
      }
      .signal-cti .break-btn.extend {
        background: transparent; color: var(--text-muted);
        border: 1px solid var(--border-strong);
      }
      .signal-cti .break-nudge {
        margin-top: 24px; padding: 12px 16px;
        background: var(--sky-soft); border-left: 3px solid var(--sky);
        border-radius: 8px; font-size: 12px; color: var(--text);
        max-width: 400px; position: relative; text-align: left;
        display: flex; gap: 10px;
      }
      .signal-cti .break-nudge svg { color: var(--sky); flex-shrink: 0; margin-top: 1px; }

      /* Responsive */
      @media (max-width: 1000px) {
        .signal-cti .idle-grid { grid-template-columns: 1fr; }
        .signal-cti .oncall-grid { grid-template-columns: 1fr; }
        .signal-cti .ringing-hero { grid-template-columns: 1fr; }
        .signal-cti .wrap-hero { grid-template-columns: 1fr; }
      }
      @media (max-width: 1023.98px) {
        .signal-cti .app { grid-template-columns: 1fr; }
        .signal-cti .sidebar { position: static; height: auto; }
        .signal-cti .topbar { flex-wrap: wrap; }
        .signal-cti .call-hud { left: 0; flex-wrap: wrap; }
      }
      @media (max-width: 767.98px) {
        .signal-cti .search { max-width: 100%; min-width: 0; }
        .signal-cti .dial-pop { width: min(320px, calc(100vw - 24px)); }
        .signal-cti .dial-pop.has-recent { width: min(560px, calc(100vw - 24px)); }
        .signal-cti .dial-pop.has-recent .dial-pop-body { grid-template-columns: 1fr; }
        .signal-cti .hud-pop { min-width: min(280px, calc(100vw - 24px)); max-width: calc(100vw - 24px); }
        .signal-cti .ringing-hero { padding: 16px; }
        .signal-cti .break-hero::before { width: min(400px, 100%); }
        .signal-cti .break-nudge { max-width: 100%; }
      }
    `}} />
  );
}
